import {
    mxCell,
    mxCellEditor,
    mxCellHighlight,
    mxCellOverlay,
    mxCellRenderer,
    mxCellState,
    mxClient,
    mxConnectionConstraint,
    mxConnectionHandler,
    mxConstants,
    mxConstraintHandler,
    mxDictionary,
    mxDragSource,
    mxEdgeHandler,
    mxElbowEdgeHandler,
    mxEllipse,
    mxEvent,
    mxEventObject,
    mxGraph,
    mxGraphHandler,
    mxGraphModel,
    mxGraphView,
    mxGuide,
    mxHierarchicalLayout,
    mxImage,
    mxImageShape,
    mxLayoutManager,
    mxOutline,
    mxPanningHandler,
    mxPoint,
    mxPolyline,
    mxPopupMenu,
    mxPopupMenuHandler,
    mxRectangle,
    mxRectangleShape,
    mxResources,
    mxRubberband,
    mxShape,
    mxShapeRegistry,
    mxStencilRegistry,
    mxStylesheet,
    mxSvgCanvas2D,
    mxUtils,
    mxVertexHandler,
} from './mxGraphFactory';
import {v4 as uuid} from 'uuid';
import {Palette} from './Palette';
import AlertCircleRedSvg from '../../images/alert-circle-red.svg';
import CallSplitTealSvg from '../../images/call-split-teal.svg';
import RefreshCircleSvg from '@mdi/svg/svg/refresh-circle.svg';
import RotateRightSvg from '@mdi/svg/svg/rotate-right.svg';
import ArrowDownCircleSvg from '../../images/arrow-down-bold-circle-outline-purple.svg';
import SetMergeOrangeSvg from '../../images/set-merge-orange.svg';
import SetSplitOrangeSvg from '../../images/set-split-orange.svg';
import CheckCircleOutlineSvg from '../../images/check-circle-outline-green.svg';
import AlertCircleOutlineSvg from '../../images/alert-circle-outline-red.svg';
import ClockSvg from '../../images/clock-outline-gray.svg';
import SpinnerSvg from '../../images/spinner-gray.svg';
import {defaultStyles} from './defaultStyles';
import {createRoute, createSvgImage, deepCopy} from './helper';
import {choiceTaskPrefix, normalizeRouteGeometryPoints, parallelTaskPrefix, taskTypeToStyle, vectorTaskImage,} from './workflow';
import {findStartTask} from './graphStartTask';
import GraphStructureValidator from './validation/GraphStructureValidator';
import ValidationContext from './validation/ValidationContext';
import isEmpty from 'lodash/isEmpty';
import {getProviderLogo} from "../Common/aiProviders";

const isStyleEnabled = value => value === true || value === 1 || value === '1';
const isStyleDisabled = value => value === false || value === 0 || value === '0';
const finiteNumber = (value, defaultValue) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : defaultValue;
};
const graphPoint = point => {
    const x = finiteNumber(point?.x, null);
    const y = finiteNumber(point?.y, null);

    return x == null || y == null ? null : new mxPoint(x, y);
};
const graphPoints = points => normalizeRouteGeometryPoints(points).map(graphPoint).filter(point => point != null);
const taskGeometry = (geometry, defaultWidth, defaultHeight) => ({
    x: finiteNumber(geometry?.x, 0),
    y: finiteNumber(geometry?.y, 0),
    width: finiteNumber(geometry?.width, defaultWidth),
    height: finiteNumber(geometry?.height, defaultHeight),
});
const hasExplicitGeometryDimension = (geometry, dimension) => {
    const value = geometry?.[dimension];
    return value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value));
};
const leftResizeHandleIndexes = new Set([0, 3, 5]);
const topResizeHandleIndexes = new Set([0, 1, 2]);

const constrainBoundsToMinimum = (bounds, minimum, index, centered) => {
    if (bounds == null || minimum == null) {
        return bounds;
    }

    const result = bounds.clone ? bounds.clone() :
        new mxRectangle(bounds.x, bounds.y, bounds.width, bounds.height);

    if (result.width < minimum.width) {
        const dx = minimum.width - result.width;

        if (centered) {
            result.x -= dx / 2;
        } else if (leftResizeHandleIndexes.has(index)) {
            result.x -= dx;
        }

        result.width = minimum.width;
    }

    if (result.height < minimum.height) {
        const dy = minimum.height - result.height;

        if (centered) {
            result.y -= dy / 2;
        } else if (topResizeHandleIndexes.has(index)) {
            result.y -= dy;
        }

        result.height = minimum.height;
    }

    return result;
};
const getStateForEventSource = (graph, evt) => {
    const source = mxEvent.getSource(evt);

    if (source == null || typeof graph.view.getCellStates !== 'function') {
        return null;
    }

    return graph.view.getCellStates()
        .find(cellState => graph.model.isVertex(cellState.cell) &&
            (cellState.shape?.node?.contains(source) ||
                cellState.text?.node?.contains(source) ||
                cellState.control?.node?.contains(source))) || null;
};
const isTouchMoveCommitEvent = (handler, evt) =>
    mxEvent.isTouchEvent(evt) ||
    evt?.pointerType === 'touch' ||
    handler?.graph?.isMouseTrigger === false;
const isTouchGraphStyle = () => Graph.touchStyle ||
    (typeof navigator !== 'undefined' &&
        (navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0));
const commitActiveTouchMove = (handler, evt) => {
    if (handler == null ||
        handler.cell == null ||
        handler.first == null ||
        (handler.shape == null && !handler.livePreviewUsed) ||
        (Math.abs(handler.currentDx || 0) === 0 && Math.abs(handler.currentDy || 0) === 0)) {
        return false;
    }

    if (handler.livePreviewUsed) {
        handler.resetLivePreview();
    }

    const scale = handler.graph.getView().scale;
    const dx = handler.roundLength(handler.currentDx / scale);
    const dy = handler.roundLength(handler.currentDy / scale);
    handler.moveCells(handler.cells, dx, dy, false, handler.target, evt);
    handler.reset();

    return true;
};
const resetGraphMouseState = graph => {
    if (graph == null) {
        return;
    }

    graph.isMouseDown = false;
    graph.isMouseTrigger = null;
    graph.lastEvent = null;
};

/**
 * Sets global constants.
 */
mxConstants.SHADOW_OPACITY = 0.25;
mxConstants.SHADOWCOLOR = '#000000';
mxGraph.prototype.pageBreakColor = '#c0c0c0';
mxGraph.prototype.pageFormat = new mxRectangle(0, 0, 850, 1100);
mxGraph.prototype.pageScale = 1;

mxGraphModel.prototype.ignoreRelativeEdgeParent = false;

mxGraphView.prototype.gridTasks = 4;
mxGraphView.prototype.minGridSize = 4;
mxGraphView.prototype.gridColor = '#e0e0e0';

mxGraphModel.prototype.createId = function () {
    return uuid();
};

mxCell.prototype.isStart = function () {
    return this.style === 'start';
};

mxCell.prototype.isEnd = function () {
    return this.value?.type === 'Terminate';
};

mxCell.prototype.isSimple = function () {
    return this.value?.type === 'Simple';
};

mxCell.prototype.isHttp = function () {
    return this.value?.type === 'Http';
};

mxCell.prototype.isAIChat = function () {
    return this.value?.type === 'AIChat';
};

mxCell.prototype.isAIVectorStore = function () {
    return this.value?.type === 'AIVectorStore';
};

mxCell.prototype.isDocumentReader = function () {
    return ['JsonDocumentReader', 'TextDocumentReader', 'HtmlDocumentReader', 'MarkdownDocumentReader', 'YamlDocumentReader'].includes(this.value?.type);
};

mxCell.prototype.isJsonConverter = function () {
    return this.value?.type === 'JsonConverter';
};

mxCell.prototype.isEmail = function () {
    return this.value?.type === 'Email';
};

mxCell.prototype.isJQ = function () {
    return this.value?.type === 'JQ';
};

mxCell.prototype.isWait = function () {
    return this.value?.type === 'Wait';
};

mxCell.prototype.isMap = function () {
    return this.value?.type === 'Map';
};

mxCell.prototype.isSplit = function () {
    return this.value?.splitType === 'And';
};

mxCell.prototype.isJoin = function () {
    return this.value?.joinType === 'And';
};

mxCell.prototype.isConditional = function () {
    return this.value?.routingType === 'Conditional';
};

mxCell.prototype.getName = function () {
    return this.value?.name;
};

export const UPDATE_CELL_VALUE = 'updateCellValue';

/**
 * Constructs a new graph instance. Note that the constructor does not take a
 * container because the graph instance is needed for creating the UI, which
 * in turn will create the container for the graph. Hence, the container is
 * assigned later in EditorUi.
 */
/**
 * Defines graph class.
 */
const createStylesheet = () => {
    const stylesheet = new mxStylesheet();

    Object.keys(defaultStyles).forEach(name => stylesheet.putCellStyle(name, mxUtils.clone(defaultStyles[name])));

    return stylesheet;
};

export class Graph extends mxGraph {
    constructor(container, stylesheet = createStylesheet()) {
        super(container, new mxGraphModel(), undefined, stylesheet);

        this.currentEdgeStyle = mxUtils.clone(this.defaultEdgeStyle);
        this.currentVertexStyle = mxUtils.clone(this.defaultVertexStyle);

        this.workflowErrors = [];
        this.taskErrors = {};
        this.routeErrors = {};
        this.taskIdMap = new Map();

        this.setConnectable(false);
        this.setDropEnabled(true);
        this.setMultigraph(false);
        this.setPanning(true);
        this.setTooltips(true);
        this.setAllowDanglingEdges(false);
        this.setAllowLoops(true);
        this.setCloneInvalidEdges(false);
        this.setConnectableEdges(false);

        this.portsEnabled = false;
        this.allowAutoPanning = true;
        this.resetEdgesOnConnect = false;
        this.constrainChildren = true;
        this.constrainRelativeChildren = true;
        this.keepEdgesInForeground = true;

        this.tooltipHandler.setEnabled(false);

        // Do not scroll after moving cells
        this.graphHandler.scrollOnMove = false;
        this.graphHandler.scaleGrid = true;
        this.graphHandler.maxLivePreview = isTouchGraphStyle() ? 0 : 50;
        this.graphHandler.allowLivePreview = !isTouchGraphStyle();
        this.connectionHandler.livePreview = true;

        // Disables cloning of connection sources by default
        this.connectionHandler.setCreateTarget(false);
        this.connectionHandler.insertBeforeSource = true;

        // Disables built-in connection starts
        this.connectionHandler.isValidSource = function (cell, me) {
            return false;
        };

        // Sets the style to be used when an elbow edge is double clicked
        this.alternateEdgeStyle = 'vertical';

        // Overrides zIndex for dragElement
        mxDragSource.prototype.dragElementZIndex = mxPopupMenu.prototype.zIndex;

        // Overrides color for virtual guides for page centers
        mxGuide.prototype.getGuideColor = function (state, horizontal) {
            return (state.cell == null) ?
                '#ffa500' /* orange */ :
                mxConstants.GUIDE_COLOR;
        };

        // Changes color of move preview for black backgrounds
        this.graphHandler.createPreviewShape = function (bounds) {
            this.previewColor = (this.graph.background === '#000000') ?
                '#ffffff' :
                mxGraphHandler.prototype.previewColor;

            return mxGraphHandler.prototype.createPreviewShape.apply(this, arguments);
        };

    const rubberBand = new mxRubberband(this);

    // Timer-based activation of outline connect in connection handler
    let startTime = new Date().getTime();
    let timeOnTarget = 0;

    const connectionHandlerMouseMove = this.connectionHandler.mouseMove;

    this.connectionHandler.mouseMove = function () {
        const prev = this.currentState;
        connectionHandlerMouseMove.apply(this, arguments);

        if (prev !== this.currentState) {
            startTime = new Date().getTime();
            timeOnTarget = 0;
        } else {
            timeOnTarget = new Date().getTime() - startTime;
        }
    };

    // Activates outline connect after 1500ms with touch event or if alt is pressed inside the shape
    // outlineConnect=0 is a custom style that means do not connect to strokes inside the shape,
    // or in other words, connect to the shape's perimeter if the highlight is under the mouse
    // (the name is because the highlight, including all strokes, is called outline in the code)
    const connectionHandleIsOutlineConnectEvent = this.connectionHandler.isOutlineConnectEvent;

    this.connectionHandler.isOutlineConnectEvent = function (me) {
        return (this.currentState != null && me.getState() === this.currentState &&
                timeOnTarget > 2000) ||
            ((this.currentState == null ||
                    mxUtils.getValue(this.currentState.style, 'outlineConnect', '1') !==
                    '0') &&
                connectionHandleIsOutlineConnectEvent.apply(this, arguments));
    };

    // Adds shift+click to toggle selection state
    const isToggleEvent = this.isToggleEvent;
    this.isToggleEvent = function (evt) {
        return isToggleEvent.apply(this, arguments) || mxEvent.isShiftDown(evt);
    };

    // Workaround for Firefox where first mouse down is received
    // after tap and hold if scrollbars are visible, which means
    // start rubberBand immediately if no cell is under mouse.
    const isForceRubberBandEvent = rubberBand.isForceRubberbandEvent;
    rubberBand.isForceRubberbandEvent = function (me) {
        return isForceRubberBandEvent.apply(this, arguments) ||
            (mxUtils.hasScrollbars(this.graph.container) && mxClient.IS_FF &&
                mxClient.IS_WIN && me.getState() == null &&
                mxEvent.isTouchEvent(me.getEvent()));
    };

    // Shows hand cursor while panning
    let prevCursor = null;

    this.panningHandler.addListener(mxEvent.PAN_START,
        mxUtils.bind(this, function () {
            if (this.isEnabled()) {
                prevCursor = this.container.style.cursor;
                this.container.style.cursor = 'move';
            }
        }));

    this.panningHandler.addListener(mxEvent.PAN_END,
        mxUtils.bind(this, function () {
            if (this.isEnabled()) {
                this.container.style.cursor = prevCursor;
            }
        }));

    this.popupMenuHandler.autoExpand = true;

    this.popupMenuHandler.isSelectOnPopup = function (me) {
        return mxEvent.isMouseEvent(me.getEvent());
    };

    // Redirects tooltips for locked cells
    this.tooltipHandler.getStateForEvent = function (me) {
        return me.sourceState;
    };

    // End-states are no valid sources
    const previousIsValidSource = this.isValidSource;
    this.isValidSource = function (cell) {
        if (previousIsValidSource.apply(this, arguments)) {
            return !cell.isEnd();
        }
        return false;
    };

    this.isValidTarget = function (cell) {
        return !cell.isStart();
    };

    const previousIsValidConnection = this.isValidConnection;
    this.isValidConnection = function (source, target) {
        if (source.isSplit() && target.isJoin()) {
            return false;
        }

        return previousIsValidConnection.apply(this, arguments);
    };

    // Redirects cursor for locked cells
    this.getCursorForMouseEvent = function (me) {
        const locked = me.state == null && me.sourceState != null &&
            this.isCellLocked(me.sourceState.cell);

        return this.getCursorForCell((locked) ? me.sourceState.cell : me.getCell());
    };

    // Changes rubberBand selection to be recursive
    this.selectRegion = function (rect, evt) {
        const cells = this.getAllCells(rect.x, rect.y, rect.width, rect.height);
        this.selectCellsForEvent(cells, evt);

        return cells;
    };

    // Recursive implementation for rubberBand selection
    this.getAllCells = function (x, y, width, height, parent, result) {
        result = (result != null) ? result : [];

        if (width > 0 || height > 0) {
            const model = this.getModel();
            const right = x + width;
            const bottom = y + height;

            if (parent == null) {
                parent = this.getCurrentRoot();

                if (parent == null) {
                    parent = model.getRoot();
                }
            }

            if (parent != null) {
                const childCount = model.getChildCount(parent);

                for (let i = 0; i < childCount; i++) {
                    const cell = model.getChildAt(parent, i);
                    const state = this.view.getState(cell);

                    if (state != null && this.isCellVisible(cell) &&
                        mxUtils.getValue(state.style, 'locked', '0') !== '1') {
                        const deg = mxUtils.getValue(state.style,
                            mxConstants.STYLE_ROTATION) || 0;
                        let box = state;

                        if (deg !== 0) {
                            box = mxUtils.getBoundingBox(box, deg);
                        }

                        if ((model.isEdge(cell) || model.isVertex(cell)) &&
                            box.x >= x && box.y + box.height <= bottom &&
                            box.y >= y && box.x + box.width <= right) {
                            result.push(cell);
                        }

                        this.getAllCells(x, y, width, height, cell, result);
                    }
                }
            }
        }

        return result;
    };

    // Never removes cells from parents that are being moved
    const graphHandlerShouldRemoveCellsFromParent = this.graphHandler.shouldRemoveCellsFromParent;
    this.graphHandler.shouldRemoveCellsFromParent = function (parent, cells, evt) {
        if (this.graph.isCellSelected(parent)) {
            return false;
        }

        return graphHandlerShouldRemoveCellsFromParent.apply(this, arguments);
    };

    // Unlocks all cells
    this.isCellLocked = function (cell) {
        let pState = this.view.getState(cell);

        while (pState != null) {
            if (mxUtils.getValue(pState.style, 'locked', '0') === '1') {
                return true;
            }

            pState = this.view.getState(this.model.getParent(pState.cell));
        }

        return false;
    };

    let tapAndHoldSelection = null;

    // Uses this event to process mouseDown to check the selection state before it is changed
    this.addListener(mxEvent.FIRE_MOUSE_EVENT,
        mxUtils.bind(this, function (sender, evt) {
            if (evt.getProperty('eventName') === 'mouseDown') {
                const me = evt.getProperty('event');
                const state = me.getState();

                if (state != null && !this.isSelectionEmpty() &&
                    !this.isCellSelected(state.cell)) {
                    tapAndHoldSelection = this.getSelectionCells();
                } else {
                    tapAndHoldSelection = null;
                }
            }
        }));

    // Tap and hold on background starts rubberBand for multiple selected
    // cells the cell associated with the event is deselected
    this.addListener(mxEvent.TAP_AND_HOLD,
        mxUtils.bind(this, function (sender, evt) {
            if (!mxEvent.isMultiTouchEvent(evt)) {
                const me = evt.getProperty('event');
                const cell = evt.getProperty('cell');

                if (cell == null) {
                    const pt = mxUtils.convertPoint(this.container,
                        mxEvent.getClientX(me), mxEvent.getClientY(me));
                    rubberBand.start(pt.x, pt.y);
                } else if (tapAndHoldSelection != null) {
                    this.addSelectionCells(tapAndHoldSelection);
                } else if (this.getSelectionCount() > 1 &&
                    this.isCellSelected(cell)) {
                    this.removeSelectionCell(cell);
                }

                // Blocks further processing of the event
                tapAndHoldSelection = null;
                evt.consume();
            }
        }));

    // On connect the target is selected and we clone the cell of the preview edge for insert
    this.connectionHandler.selectCells = function (edge, target) {
        this.graph.clearSelfLoopConnectionConstraints(edge);
        this.graph.setSelectionCell(target || edge);
    };

    // Shows connection points only if cell not selected
    this.connectionHandler.constraintHandler.isStateIgnored = function (
        state, source) {
        const activeConnection = state.view.graph.connectionHandler?.previous?.cell ===
            state.cell;

        return source && state.view.graph.isCellSelected(state.cell) &&
            !activeConnection;
    };

    // Updates constraint handler if the selection changes
    this.selectionModel.addListener(mxEvent.CHANGE,
        mxUtils.bind(this, function () {
            const ch = this.connectionHandler.constraintHandler;

            if (ch.currentFocus != null &&
                ch.isStateIgnored(ch.currentFocus, true)) {
                ch.currentFocus = null;
                ch.constraints = null;
                ch.destroyIcons();
            }

            ch.destroyFocusHighlight();
        }));

    // Initializes touch interface
    if (Graph.touchStyle) {
        this.initTouch();
    }

    /**
     * Adds locking
     */
    const graphUpdateMouseEvent = this.updateMouseEvent;
    this.updateMouseEvent = function (me) {
        me = graphUpdateMouseEvent.apply(this, arguments);

        if (me.state != null && this.isCellLocked(me.getCell())) {
            me.state = null;
        }

        return me;
    };

    // Create a unique offset object for each graph instance.
    this.currentTranslate = new mxPoint(0, 0);

    this.layoutManager = new mxLayoutManager(this);
    this.commitTouchMoveHandler = evt => {
        if (isTouchMoveCommitEvent(this.graphHandler, evt) &&
            commitActiveTouchMove(this.graphHandler, evt)) {
            resetGraphMouseState(this);
            mxEvent.consume(evt);
        }
    };
    document.addEventListener('touchend', this.commitTouchMoveHandler, {capture: true});
    document.addEventListener('touchcancel', this.commitTouchMoveHandler, {capture: true});
    document.addEventListener('pointerup', this.commitTouchMoveHandler, {capture: true});
    document.addEventListener('pointercancel', this.commitTouchMoveHandler, {capture: true});
    }
}

const graphDestroy = mxGraph.prototype.destroy;
Graph.prototype.destroy = function () {
    if (this.commitTouchMoveHandler) {
        document.removeEventListener('touchend', this.commitTouchMoveHandler, {capture: true});
        document.removeEventListener('touchcancel', this.commitTouchMoveHandler, {capture: true});
        document.removeEventListener('pointerup', this.commitTouchMoveHandler, {capture: true});
        document.removeEventListener('pointercancel', this.commitTouchMoveHandler, {capture: true});
        this.commitTouchMoveHandler = null;
    }

    return graphDestroy.apply(this, arguments);
};

/**
 * Default size for line jumps.
 */
Graph.lineJumpsEnabled = true;

/**
 * Default size for line jumps.
 */
Graph.defaultJumpSize = 6;

Graph.touchStyle = mxClient.IS_TOUCH || (mxClient.IS_FF && mxClient.IS_WIN) ||
    navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;

/**
 * Scrollbars are enabled on non-touch devices (not including Firefox because touch events
 * cannot be detected in Firefox, see above).
 */
Graph.prototype.defaultScrollbars = !mxClient.IS_IOS;

/**
 *
 */
Graph.prototype.defaultPageBackgroundColor = '#ffffff';

/**
 *
 */
Graph.prototype.defaultPageBorderColor = '#ffffff';

/**
 *
 */
Graph.prototype.defaultGraphBackground = '#ffffff';

/**
 * Specifies the size of the size for "tiles" to be used for a graph with
 * scrollbars but no visible background page. A good value is large
 * enough to reduce the number of repaints that is caused for auto-
 * translation, which depends on this value, and small enough to give
 * a small empty buffer around the graph. Default is 400x400.
 */
Graph.prototype.scrollTileSize = new mxRectangle(0, 0, 400, 400);

/**
 * Overrides the background color and paints a transparent background.
 */
Graph.prototype.transparentBackground = false;

/**
 * Sets the default target for all links in cells.
 */
Graph.prototype.defaultEdgeLength = 80;

/**
 * Allows all values in fit.
 */
Graph.prototype.connectionArrowsEnabled = true;

Graph.prototype.editable = true;

const graphCreateVertexHandler = mxGraph.prototype.createVertexHandler;
Graph.prototype.createVertexHandler = function () {
    const handler = graphCreateVertexHandler.apply(this, arguments);

    handler.handleImage = HoverIcons.prototype.mainHandle;
    handler.secondaryHandleImage = HoverIcons.prototype.secondaryHandle;
    handler.manageSizers = true;
    handler.livePreview = !isTouchGraphStyle();

    return handler;
};

const graphCreateEdgeHandler = mxGraph.prototype.createEdgeHandler;
Graph.prototype.createEdgeHandler = function () {
    const handler = graphCreateEdgeHandler.apply(this, arguments);

    handler.handleImage = HoverIcons.prototype.mainHandle;
    handler.terminalHandleImage = HoverIcons.prototype.terminalHandle;
    handler.fixedHandleImage = HoverIcons.prototype.fixedHandle;
    handler.labelHandleImage = HoverIcons.prototype.secondaryHandle;
    handler.parentHighlightEnabled = true;
    handler.dblClickRemoveEnabled = true;
    handler.straightRemoveEnabled = true;
    handler.mergeRemoveEnabled = true;
    handler.manageLabelHandle = true;
    handler.outlineConnect = true;
    handler.snapToTerminals = true;

    return handler;
};

Graph.prototype.getMinimumVertexSize = function (cell, state = this.view.getState(cell)) {
    if (!cell?.isVertex?.() || cell.isStart?.() || cell.isEnd?.()) {
        return null;
    }

    const style = state?.style || this.getCellStyle(cell);
    const label = this.convertValueToString(cell);
    const fontSize = finiteNumber(style.fontSize, 14);
    const textSize = typeof mxUtils.getSizeForString === 'function' ?
        mxUtils.getSizeForString(label, fontSize, style.fontFamily, null, style.fontStyle) :
        this.getPreferredSizeForCell(cell);
    const imageWidth = style.image ? finiteNumber(style.imageWidth, 0) : 0;
    const imageHeight = style.image ? finiteNumber(style.imageHeight, imageWidth) : 0;
    const paddingLeft = 4;
    const paddingRight = 10;
    const paddingVertical = 4;
    const spacingLeft = finiteNumber(style.spacingLeft, 0);
    const spacingRight = finiteNumber(style.spacingRight, 0);
    const spacingTop = finiteNumber(style.spacingTop, 0);
    const spacingBottom = finiteNumber(style.spacingBottom, 0);
    const minWidth = Math.ceil((textSize?.width || 0) +
        Math.max(spacingLeft, imageWidth) + spacingRight + paddingLeft + paddingRight);
    const minHeight = Math.ceil(Math.max(imageHeight,
        (textSize?.height || 0) + spacingTop + spacingBottom) + (2 * paddingVertical));

    return new mxRectangle(0, 0, minWidth, minHeight);
};

Graph.prototype.constrainVertexBoundsToMinimum = function (cell, bounds) {
    if (bounds == null || !cell?.isVertex?.()) {
        return bounds;
    }

    return constrainBoundsToMinimum(bounds, this.getMinimumVertexSize(cell), 7, false);
};

Graph.prototype.constrainMissingVertexGeometryToMinimum = function (cell, geometry) {
    const hasWidth = hasExplicitGeometryDimension(geometry, 'width');
    const hasHeight = hasExplicitGeometryDimension(geometry, 'height');
    if (cell?.geometry == null || (hasWidth && hasHeight)) {
        return;
    }

    const constrained = this.constrainVertexBoundsToMinimum(cell, cell.geometry);
    if (constrained == null) {
        return;
    }

    const updated = cell.geometry.clone();
    let changed = false;

    if (!hasWidth && constrained.width > updated.width) {
        updated.width = constrained.width;
        changed = true;
    }

    if (!hasHeight && constrained.height > updated.height) {
        updated.height = constrained.height;
        changed = true;
    }

    if (changed) {
        this.getModel().setGeometry(cell, updated);
    }
};

Graph.prototype.clearSelfLoopConnectionConstraints = function (edge) {
    const source = edge?.getTerminal?.(true);

    if (source == null || source !== edge.getTerminal(false)) {
        return;
    }

    this.setConnectionConstraint(edge, source, true, null);
    this.setConnectionConstraint(edge, source, false, null);
};

const graphResizeCells = mxGraph.prototype.resizeCells;
Graph.prototype.resizeCells = function (cells, bounds, recurse) {
    const constrainedBounds = (bounds || []).map((cellBounds, index) =>
        this.constrainVertexBoundsToMinimum(cells?.[index], cellBounds));

    return graphResizeCells.call(this, cells, constrainedBounds, recurse);
};

/**
 * Installs child layout styles.
 */
Graph.prototype.init = function (container) {
    if (typeof mxGraph.prototype.init === 'function') {
        mxGraph.prototype.init.apply(this, arguments);
    } else if (container) {
        this.container = container;
    }

    this.workflowErrors = [];
    this.taskErrors = {};
    this.routeErrors = {};
    this.mapErrorIds = new Set();
};

Graph.prototype.setWorkflowErrors = function (workflowErrors) {
    this.workflowErrors = workflowErrors || [];
};

const applyMapErrors = (cell, graph) => {
    graph.mapErrorIds = graph.mapErrorIds || new Set();
    const model = graph.getModel();
    const parentId = cell.getParent()?.id;
    if (parentId) {
        const layerCount = model.getChildCount(model.getRoot());
        for (let i = layerCount - 1; i >= 0; i--) {
            const layer = model.getChildAt(model.getRoot(), i);
            const layerChildCount = model.getChildCount(layer);
            for (let j = layerChildCount - 1; j >= 0; j--) {
                const layerChild = model.getChildAt(layer, j);
                if (layerChild.isMap() && layerChild.getValue().itemProcessorId === parentId) {
                    if (!graph.mapErrorIds.has(layerChild.id)) {
                        graph.mapErrorIds.add(layerChild.id);
                        graph.addCellOverlay(layerChild, graph.errorOverlay(mxResources.get('subMapErrors')));
                    }
                    applyMapErrors(layerChild, graph);
                }
            }
        }
    }
}

Graph.prototype.setTaskErrors = function (taskErrors) {
    this.taskErrors = taskErrors || {};
    Object.keys(this.taskErrors).forEach(id => {
        if (id && this.getModel().getCell(id)) {
            const cell = this.getModel().getCell(id);
            this.addCellOverlay(cell, this.errorOverlay(this.taskErrors[id].message || mxResources.get('taskErrors')));

            applyMapErrors(cell, this);
        }
    });
};

Graph.prototype.setRouteErrors = function (routeErrors) {
    this.routeErrors = routeErrors || {};
    Object.keys(this.routeErrors).forEach(id => {
        if (id && this.getModel().getCell(id)) {
            const cell = this.getModel().getCell(id);
            const errorOverlay = this.errorOverlay(
                this.routeErrors[id].message || mxResources.get('routeErrors'));
            errorOverlay.align = null;
            errorOverlay.verticalAlign = null;
            errorOverlay.offset = new mxPoint(0, 23);
            this.addCellOverlay(cell, errorOverlay);

            applyMapErrors(cell, this);
        }
    });
};

Graph.prototype.clearAllErrors = function () {
    this.workflowErrors = [];
    this.taskErrors = {};
    this.routeErrors = {};
    this.mapErrorIds = new Set();
    this.clearCellErrorOverlays(this.getModel().getRoot());
};

Graph.prototype.getWorkflowErrors = function () {
    return this.workflowErrors || [];
};

Graph.prototype.getTaskErrors = function (id) {
    return this.taskErrors[id] || {};
};

Graph.prototype.getRouteErrors = function (id) {
    return this.routeErrors[id] || {};
};

/**
 * Implements zoom and offset via CSS transforms. This is currently only used
 * in read-only as there are fewer issues with the mxCellState not being scaled
 * and translated.
 *
 * KNOWN ISSUES TO FIX:
 * - Apply CSS transforms to HTML labels in IE11
 */
(function () {
    /**
     * Uses CSS transforms for scale and translate.
     */
    Graph.prototype.useCssTransforms = false;

    /**
     * Contains the scale.
     */
    Graph.prototype.currentScale = 1;

    /**
     * Contains the offset.
     */
    Graph.prototype.currentTranslate = new mxPoint(0, 0);

    Graph.prototype.getVerticesAndEdges = function (vertices, edges) {
        vertices = (vertices != null) ? vertices : true;
        edges = (edges != null) ? edges : true;
        const model = this.model;

        return model.filterDescendants(function (cell) {
            return (vertices && model.isVertex(cell)) ||
                (edges && model.isEdge(cell));
        }, model.getRoot());
    };

    /**
     * Only foreignObject supported for now (no IE11).
     */
    Graph.prototype.isCssTransformsSupported = function () {
        return this.dialect === mxConstants.DIALECT_SVG && !mxClient.NO_FO;
    };

    /**
     * Function: getCellAt
     *
     * Needs to modify original method for recursive call.
     */
    Graph.prototype.getCellAt = function (
        x, y, parent, vertices, edges, ignoreFn) {
        if (this.useCssTransforms) {
            x = x / this.currentScale - this.currentTranslate.x;
            y = y / this.currentScale - this.currentTranslate.y;
        }

        return this.getScaledCellAt.apply(this, arguments);
    };

    /**
     * Function: getScaledCellAt
     *
     * Overridden for recursion.
     */
    Graph.prototype.getScaledCellAt = function (
        x, y, parent, vertices, edges, ignoreFn) {
        vertices = (vertices != null) ? vertices : true;
        edges = (edges != null) ? edges : true;

        if (parent == null) {
            parent = this.getCurrentRoot();

            if (parent == null) {
                parent = this.getModel().getRoot();
            }
        }

        if (parent != null) {
            const childCount = this.model.getChildCount(parent);

            for (let i = childCount - 1; i >= 0; i--) {
                const cell = this.model.getChildAt(parent, i);
                const result = this.getScaledCellAt(x, y, cell, vertices, edges,
                    ignoreFn);

                if (result != null) {
                    return result;
                } else if (this.isCellVisible(cell) && (edges &&
                    (this.model.isEdge(cell) ||
                        (vertices && this.model.isVertex(cell))))) {
                    const state = this.view.getState(cell);

                    if (state != null && (ignoreFn == null || !ignoreFn(state, x, y)) &&
                        this.intersects(state, x, y)) {
                        return cell;
                    }
                }
            }
        }

        return null;
    };

    Graph.prototype.isPart = function (cell) {
        return mxUtils.getValue(this.getCurrentCellStyle(cell), 'part', '0') === '1';
    };

    Graph.prototype.getCompositeParent = function (cell) {
        while (this.isPart(cell)) {
            const temp = this.model.getParent(cell);

            if (!this.model.isVertex(temp)) {
                break;
            }

            cell = temp;
        }

        return cell;
    };

    /**
     * Function: repaint
     *
     * Updates the highlight after a change of the model or view.
     */
    mxCellHighlight.prototype.getStrokeWidth = function (state) {
        let s = this.strokeWidth;

        if (this.graph.useCssTransforms) {
            s /= this.graph.currentScale;
        }

        return s;
    };

    /**
     * Function: getGraphBounds
     *
     * Overrides getGraphBounds to use bounding box from SVG.
     */
    mxGraphView.prototype.getGraphBounds = function () {
        let b = this.graphBounds;

        if (this.graph.useCssTransforms) {
            const t = this.graph.currentTranslate;
            const s = this.graph.currentScale;

            b = new mxRectangle(
                (b.x + t.x) * s, (b.y + t.y) * s,
                b.width * s, b.height * s);
        }

        return b;
    };

    /**
     * Function: viewStateChanged
     *
     * Overrides to bypass full cell tree validation.
     * TODO: Check if this improves performance
     */
    mxGraphView.prototype.viewStateChanged = function () {
        if (this.graph.useCssTransforms) {
            this.validate();
            this.graph.sizeDidChange();
        } else {
            this.revalidate();
            this.graph.sizeDidChange();
        }
    };

    /**
     * Function: validate
     *
     * Overrides validate to normalize validation view state and pass
     * current state to CSS transform.
     */
    const graphViewValidate = mxGraphView.prototype.validate;

    mxGraphView.prototype.validate = function (cell) {
        if (this.graph.useCssTransforms) {
            this.graph.currentScale = this.scale;
            this.graph.currentTranslate.x = this.translate.x;
            this.graph.currentTranslate.y = this.translate.y;

            this.scale = 1;
            this.translate.x = 0;
            this.translate.y = 0;
        }

        graphViewValidate.apply(this, arguments);

        if (this.graph.useCssTransforms) {
            this.graph.updateCssTransform();

            this.scale = this.graph.currentScale;
            this.translate.x = this.graph.currentTranslate.x;
            this.translate.y = this.graph.currentTranslate.y;
        }
    };

    /**
     * Function: updateCssTransform
     *
     * Zooms out of the graph by <zoomFactor>.
     */
    Graph.prototype.updateCssTransform = function () {
        const temp = this.view.getDrawPane();

        if (temp != null) {
            const g = this.view.getDrawPane().parentNode;
            const prev = g.getAttribute('transform');
            g.setAttribute('transformOrigin', '0 0');
            g.setAttribute('transform',
                'scale(' + this.currentScale + ',' + this.currentScale + ')' +
                'translate(' + this.currentTranslate.x + ',' +
                this.currentTranslate.y + ')');

            // Applies workarounds only if translate has changed
            if (prev !== g.getAttribute('transform')) {
                try {
                    if (mxClient.IS_EDGE) {
                        // Recommended workaround is to do this on all
                        // foreignObjects, but this seems to be faster
                        const val = g.style.display;
                        g.style.display = 'none';
                        g.getBBox();
                        g.style.display = val;
                    }
                } catch (e) {
                    // ignore
                }
            }
        }
    };

    const graphViewValidateBackgroundPage = mxGraphView.prototype.validateBackgroundPage;

    mxGraphView.prototype.validateBackgroundPage = function () {
        const useCssTranforms = this.graph.useCssTransforms, scale = this.scale,
            translate = this.translate;

        if (useCssTranforms) {
            this.scale = this.graph.currentScale;
            this.translate = this.graph.currentTranslate;
        }

        graphViewValidateBackgroundPage.apply(this, arguments);

        if (useCssTranforms) {
            this.scale = scale;
            this.translate = translate;
        }
    };

    const graphViewIsLoopStyleEnabled = mxGraphView.prototype.isLoopStyleEnabled;
    mxGraphView.prototype.isLoopStyleEnabled = function (edge, points = [], source = null, target = null) {
        if ((points == null || points.length < 2) && source != null && source === target) {
            return true;
        }

        return graphViewIsLoopStyleEnabled.apply(this, arguments);
    };
})();

/**
 * Returns the size of the page format scaled with the page size.
 */
Graph.prototype.getPageSize = function () {
    return this.scrollTileSize;
};

/**
 * Returns a rectangle describing the position and count of the
 * background pages, where x and y are the position of the top,
 * left page and width and height are the vertical and horizontal
 * page count.
 */
Graph.prototype.getPageLayout = function () {
    const size = this.getPageSize();
    const bounds = this.getGraphBounds();

    if (bounds.width === 0 || bounds.height === 0) {
        return new mxRectangle(0, 0, 1, 1);
    } else {
        // Computes untransformed graph bounds
        const x = Math.ceil(bounds.x / this.view.scale - this.view.translate.x);
        const y = Math.ceil(bounds.y / this.view.scale - this.view.translate.y);
        const w = Math.floor(bounds.width / this.view.scale);
        const h = Math.floor(bounds.height / this.view.scale);

        const x0 = Math.floor(x / size.width);
        const y0 = Math.floor(y / size.height);
        const w0 = Math.ceil((x + w) / size.width) - x0;
        const h0 = Math.ceil((y + h) / size.height) - y0;

        return new mxRectangle(x0, y0, w0, h0);
    }
};

/**
 * Returns true if the given mouse wheel event should be used for zooming. This
 * is invoked if no dialogs are showing and returns true with Alt or Control
 * (except macOS) is pressed.
 */
Graph.prototype.isZoomWheelEvent = function (evt) {
    return mxEvent.isAltDown(evt) ||
        (mxEvent.isMetaDown(evt) && mxClient.IS_MAC) ||
        (mxEvent.isControlDown(evt) && !mxClient.IS_MAC);
};

/**
 * Adds Alt+click to select cells behind cells.
 */
Graph.prototype.isTransparentClickEvent = function (evt) {
    return mxEvent.isAltDown(evt);
};

/**
 * Adds ctrl+shift+connect to disable connections.
 */
Graph.prototype.isIgnoreTerminalEvent = function (evt) {
    return mxEvent.isShiftDown(evt) && mxEvent.isControlDown(evt);
};

Graph.prototype.isSplitTarget = function (target, cells, evt) {
    return target != null &&
        !this.model.isEdge(cells[0]) &&
        !mxEvent.isAltDown(evt) && !mxEvent.isShiftDown(evt) &&
        mxGraph.prototype.isSplitTarget.apply(this, arguments);
};

/**
 * Adds labelMovable style.
 */
Graph.prototype.isLabelMovable = function (cell) {
    const state = this.view.getState(cell);
    const style = (state != null) ? state.style : this.getCellStyle(cell);

    return !this.isCellLocked(cell) &&
        ((this.model.isEdge(cell) && this.edgeLabelsMovable) ||
            (this.model.isVertex(cell) && (this.vertexLabelsMovable ||
                mxUtils.getValue(style, 'labelMovable', '0') === '1')));
};

Graph.prototype.selectCellsForConnectVertex = function (cells, evt, hoverIcons) {
    if (cells.length === 2 && this.model.isVertex(cells[1])) {
        this.setSelectionCell(cells[1]);
        this.scrollCellToVisible(cells[1]);

        if (hoverIcons != null) {
            // Adds hover icons for cloned vertex or hides icons
            if (mxEvent.isTouchEvent(evt)) {
                hoverIcons.update(hoverIcons.getState(this.view.getState(cells[1])));
            } else {
                hoverIcons.reset();
            }
        }
    } else {
        this.setSelectionCells(cells);
    }
};

Graph.prototype.isCloneConnectSource = function (source) {
    let layout = null;

    if (this.layoutManager != null) {
        layout = this.layoutManager.getLayout(this.model.getParent(source));
    }

    return (layout != null && layout.constructor === mxStackLayout);
};

Graph.prototype.connectVertex = function (source, direction, length, evt, forceClone, ignoreCellAt, createTarget, done) {
    ignoreCellAt = (ignoreCellAt) ? ignoreCellAt : false;

    // Ignores relative edge labels
    if (source.geometry.relative && this.model.isEdge(source.parent)) {
        return [];
    }

    // Uses parent for relative child cells
    while (source.geometry.relative && this.model.isVertex(source.parent)) {
        source = source.parent;
    }

    // Handles clone connect sources
    const cloneSource = this.isCloneConnectSource(source);
    const composite = (cloneSource) ? source : this.getCompositeParent(source);

    const pt = (source.geometry.relative && source.parent.geometry != null) ?
        new mxPoint(source.parent.geometry.width * source.geometry.x,
            source.parent.geometry.height * source.geometry.y) :
        new mxPoint(composite.geometry.x, composite.geometry.y);

    if (direction === mxConstants.DIRECTION_NORTH) {
        pt.x += composite.geometry.width / 2;
        pt.y -= length;
    } else if (direction === mxConstants.DIRECTION_SOUTH) {
        pt.x += composite.geometry.width / 2;
        pt.y += composite.geometry.height + length;
    } else if (direction === mxConstants.DIRECTION_WEST) {
        pt.x -= length;
        pt.y += composite.geometry.height / 2;
    } else {
        pt.x += composite.geometry.width + length;
        pt.y += composite.geometry.height / 2;
    }

    const parentState = this.view.getState(this.model.getParent(source));
    const s = this.view.scale;
    const t = this.view.translate;
    let dx = t.x * s;
    let dy = t.y * s;

    if (parentState != null && this.model.isVertex(parentState.cell)) {
        dx = parentState.x;
        dy = parentState.y;
    }

    // Workaround for relative child cells
    if (this.model.isVertex(source.parent) && source.geometry.relative) {
        pt.x += source.parent.geometry.x;
        pt.y += source.parent.geometry.y;
    }

    // Checks actual end point of edge for target cell
    const rect = (!ignoreCellAt) ? new mxRectangle(dx + pt.x * s, dy + pt.y * s).grow(40) : null;
    const tempCells = (rect != null) ? this.getCells(0, 0, 0, 0, null, null, rect) : null;
    let target = (tempCells != null && tempCells.length > 0) ? tempCells.reverse()[0] : null;
    let keepParent = false;

    if (target != null && this.model.isAncestor(target, source)) {
        keepParent = true;
        target = null;
    }

    // Checks for swimlane at drop location
    if (target == null) {
        const temp = this.getSwimlaneAt(dx + pt.x * s, dy + pt.y * s);

        if (temp != null) {
            keepParent = false;
            target = temp;
        }
    }

    // Checks if target or ancestor is locked
    let temp = target;

    while (temp != null) {
        if (this.isCellLocked(temp)) {
            target = null;
            break;
        }

        temp = this.model.getParent(temp);
    }

    // Checks if source and target intersect
    if (target != null) {
        const sourceState = this.view.getState(source);
        const targetState = this.view.getState(target);

        if (sourceState != null && targetState != null && mxUtils.intersects(sourceState, targetState)) {
            target = null;
        }
    }

    const duplicate = (!mxEvent.isShiftDown(evt) || mxEvent.isControlDown(evt)) || forceClone;

    if (duplicate) {
        if (direction === mxConstants.DIRECTION_NORTH) {
            pt.y -= source.geometry.height / 2;
        } else if (direction === mxConstants.DIRECTION_SOUTH) {
            pt.y += source.geometry.height / 2;
        } else if (direction === mxConstants.DIRECTION_WEST) {
            pt.x -= source.geometry.width / 2;
        } else {
            pt.x += source.geometry.width / 2;
        }
    }

    if (target != null && !this.isCellConnectable(target) && !this.isSwimlane(target)) {
        let parent = this.getModel().getParent(target);

        if (this.getModel().isVertex(parent) && this.isCellConnectable(parent)) {
            target = parent;
        }
    }

    if (target === source || this.model.isEdge(target) ||
        !this.isCellConnectable(target) &&
        !this.isSwimlane(target)) {
        target = null;
    }

    const result = [];
    const swimlane = target != null && this.isSwimlane(target);
    let realTarget = (!swimlane) ? target : null;

    const execute = mxUtils.bind(this, function (targetCell) {
        if (createTarget == null || targetCell != null || (target == null && cloneSource)) {
            this.model.beginUpdate();
            try {
                if (realTarget == null && duplicate) {
                    // Handles relative children
                    let cellToClone = (targetCell != null) ? targetCell : source;
                    let geo = this.getCellGeometry(cellToClone);

                    while (geo != null && geo.relative) {
                        cellToClone = this.getModel().getParent(cellToClone);
                        geo = this.getCellGeometry(cellToClone);
                    }

                    // Handles composite cells for cloning
                    cellToClone = (cloneSource) ? source : this.getCompositeParent(cellToClone);
                    realTarget = (targetCell != null) ? targetCell : this.duplicateCells([cellToClone], false)[0];

                    if (targetCell != null) {
                        this.addCells([realTarget], this.model.getParent(source), null, null, null, true);
                    }

                    geo = this.getCellGeometry(realTarget);

                    if (geo != null) {
                        geo.x = pt.x - geo.width / 2;
                        geo.y = pt.y - geo.height / 2;
                    }

                    if (swimlane) {
                        this.addCells([realTarget], target, null, null, null, true);
                        target = null;
                    } else if (duplicate && target == null && !keepParent && !cloneSource) {
                        this.addCells([realTarget], this.getDefaultParent(), null, null, null, true);
                    }
                }

                const edge = ((mxEvent.isControlDown(evt) && mxEvent.isShiftDown(evt) && duplicate) ||
                    (target == null && cloneSource)) ? null : this.insertEdge(this.model.getParent(source),
                    null, '', source, realTarget, this.createCurrentEdgeStyle());

                // Inserts edge before source
                if (edge != null && this.connectionHandler.insertBeforeSource) {
                    const index = null;
                    let tmp = source;

                    while (tmp.parent != null && tmp.geometry != null &&
                    tmp.geometry.relative && tmp.parent !== edge.parent) {
                        tmp = this.model.getParent(tmp);
                    }

                    if (tmp.parent != null && tmp.parent === edge.parent) {
                        const index = tmp.parent.getIndex(tmp);
                        this.model.add(tmp.parent, edge, index);
                    }
                }

                // Special case: Click on west icon puts clone before cell
                if (target == null && realTarget != null && source.parent != null &&
                    cloneSource && direction === mxConstants.DIRECTION_WEST) {
                    const index = source.parent.getIndex(source);
                    this.model.add(source.parent, realTarget, index);
                }

                if (edge != null) {
                    result.push(edge);
                }

                if (target == null && realTarget != null) {
                    result.push(realTarget);
                }

                if (realTarget == null && edge != null) {
                    edge.geometry.setTerminalPoint(pt, false);
                }

                if (edge != null) {
                    this.fireEvent(new mxEventObject('cellsInserted', 'cells', [edge]));
                }
            } finally {
                this.model.endUpdate();
            }
        }

        if (done != null) {
            done(result);
        } else {
            return result;
        }
    });

    if (createTarget != null && realTarget == null && duplicate &&
        (target != null || !cloneSource)) {
        createTarget(dx + pt.x * s, dy + pt.y * s, execute);
    } else {
        return execute(realTarget);
    }
};

Graph.prototype.createEdge = function (
    parent, id, value, source, target, style) {
    if ((!value || typeof value === 'string') && source && target) {
        value = createRoute(value);

        const hasUnconditional = !source.isSplit() && source.edges &&
            source.edges.some(
                edge => edge.source.id === source.id && !edge.isConditional());
        if (hasUnconditional) {
            value.routingType = 'Conditional';
        }
    }

    return mxGraph.prototype.createEdge.call(this, parent, id, value, source,
        target, style);
};

Graph.prototype.getAscendantCells = (cell) => {
    const ascendantCells = (cell, cells = []) => {
        if (cell && !cells.some(task => task.id === cell.id)) {
            if (cell.vertex) {
                cells.unshift(cell);

                if (cell.edges) {
                    cell.edges.map(edge => edge.source).forEach(source => {
                        ascendantCells(source, cells);
                    });
                }
            } else if (cell.edge) {
                ascendantCells(cell.source, cells);
            }
        }

        return cells;
    };

    return ascendantCells(cell);
};

/**
 * Adds Shift+collapse/expand and size management for folding inside stack
 */
Graph.prototype.isMoveCellsEvent = function (evt) {
    return mxEvent.isShiftDown(evt);
};

/**
 * Returns the label for the given cell.
 */
Graph.prototype.convertValueToString = function (cell) {
    const value = this.model.getValue(cell);

    if (value != null && value.hasOwnProperty('name')) {
        return value.name || '';
    }

    return mxGraph.prototype.convertValueToString.apply(this, arguments);
};

/**
 * Adds a connectable style.
 */
Graph.prototype.isCellConnectable = function (cell) {
    if (cell?.isEnd?.()) {
        return false;
    }

    const state = this.view.getState(cell);
    const style = (state != null) ? state.style : this.getCellStyle(cell);

    return (style['connectable'] != null) ?
        !isStyleDisabled(style['connectable']) :
        mxGraph.prototype.isCellConnectable.apply(this, arguments);
};

Graph.prototype.overlayWorkflowTasks = function (workflowTasks) {
    const model = this.getModel();
    const workflowTaskNames = workflowTasks.map(workflowTask => workflowTask.taskName);

    model.beginUpdate();
    try {
        workflowTasks.forEach((workflowTask, index) => {
            const takeName = workflowTask.taskName.replace(
                new RegExp(`^${choiceTaskPrefix}|${parallelTaskPrefix}`), '');
            const nodeId = this.taskIdMap.get(takeName);
            if (nodeId) {
                const node = model.getCell(nodeId);

                this.removeCellOverlay(node, this.checkCircleOverlay());
                this.removeCellOverlay(node, this.alertCircleOverlay());
                this.removeCellOverlay(node, this.clockOverlay());
                this.removeCellOverlay(node, this.spinnerOverlay());

                if (['Scheduled'].includes(workflowTask.status)) {
                    this.addCellOverlay(node, this.clockOverlay(index));
                } else if (['InProgress'].includes(workflowTask.status)) {
                    this.addCellOverlay(node, this.spinnerOverlay(index));
                } else if (['Completed', 'CompletedWithErrors'].includes(
                    workflowTask.status)) {
                    this.addCellOverlay(node, this.checkCircleOverlay(index));
                } else {
                    this.addCellOverlay(node, this.alertCircleOverlay(index));
                }

                model.getEdges(node, false, true, true).forEach(outEdge => {
                    if (workflowTaskNames.includes(outEdge.target.getValue().name)) {
                        const checkCircleOverlay = this.checkCircleOverlay();
                        checkCircleOverlay.align = mxConstants.ALIGN_CENTER;
                        checkCircleOverlay.offset = new mxPoint(0, 22);
                        this.addCellOverlay(outEdge, checkCircleOverlay);
                    }
                });
            }
        });
    } finally {
        model.endUpdate();
    }
};

Graph.prototype.setWorkflow = function (workflow) {
    const model = this.getModel();
    const workflowCell = model.getRoot();

    model.setValue(workflowCell, {
        name: workflow.name,
        description: workflow.description,
        timeoutSeconds: workflow.timeoutSeconds,
        failureWorkflow: workflow.failureWorkflow,
        failureWorkflowVersion: workflow.failureWorkflowVersion,
        maxConcurrency: workflow.maxConcurrency,
        resources: workflow.resources,
        workflowStartPage: workflow.workflowStartPage,
    });

    model.beginUpdate();
    try {
        workflow.children?.forEach((child, index) => {
            const childParent = child.id ? model.getCell(child.id) : null;
            this.setDefaultParent(childParent || model.getRoot().children[index]);
            const parent = this.getDefaultParent();
            let autoLayout = false;

            let startTask = findStartTask(child.startTask, child.tasks, child.routes, index, workflow.geometry);
            if (!parent.getValue()?.startTask) {
                model.setValue(parent, {
                    ...parent.getValue(),
                    startTask: startTask.id
                });
            }

            const startGeometry = taskGeometry(startTask.geometry, 30, 30);
            const start = this.insertVertex(parent, startTask.id, {
                name: startTask.name,
                description: startTask.description,
                splitType: startTask.splitType,
                joinTask: startTask.joinTask,
                joinType: startTask.joinType,
                inputParameters: startTask.inputParameters,
                processor: startTask.processor,
                retryIntervalSeconds: startTask.retryIntervalSeconds,
                retryMaxAttempts: startTask.retryMaxAttempts,
                retryBackoffRate: startTask.retryBackoffRate,
                decisionTask: startTask.decisionTask,
                parallelTask: startTask.parallelTask,
            }, startGeometry.x, startGeometry.y, startGeometry.width, startGeometry.height, 'start');

            if (start.isSplit() && start.isStart()) {
                this.addCellOverlay(start, this.forkOverlay(-15, -34));
            }

            this.taskIdMap.set(start.getValue().name, start.id);

            child.tasks.forEach(task => {
                const {id, geometry, style: graphStyle, ...workflowTask} = task;
                if (id === startTask.id) {
                    return;
                }

                autoLayout = !geometry;

                const style = taskTypeToStyle[workflowTask.type];
                if (graphStyle === 'start') {
                    const geometryValues = taskGeometry(geometry, 30, 30);
                    const startCell = this.insertVertex(parent, id, {
                        name: workflowTask.name,
                        description: workflowTask.description,
                        splitType: workflowTask.splitType,
                        joinTask: workflowTask.joinTask,
                        joinType: workflowTask.joinType,
                        inputParameters: workflowTask.inputParameters,
                        processor: workflowTask.processor,
                        retryIntervalSeconds: workflowTask.retryIntervalSeconds,
                        retryMaxAttempts: workflowTask.retryMaxAttempts,
                        retryBackoffRate: workflowTask.retryBackoffRate,
                        decisionTask: workflowTask.decisionTask,
                        parallelTask: workflowTask.parallelTask,
                    }, geometryValues.x, geometryValues.y, geometryValues.width, geometryValues.height, 'start');

                    if (startCell.isSplit() && startCell.isStart()) {
                        this.addCellOverlay(startCell, this.forkOverlay(-15, -34));
                    }

                    this.taskIdMap.set(startCell.getValue().name, startCell.id);
                } else if (style) {
                    const geometryValues = taskGeometry(geometry, style.width, style.height);
                    const cell = this.insertVertex(parent, id, workflowTask, geometryValues.x, geometryValues.y,
                        geometryValues.width, geometryValues.height, style.style);
                    this.constrainMissingVertexGeometryToMinimum(cell, geometry);
                    this.taskIdMap.set(cell.getValue().name, cell.id);

                    if (cell.isAIChat()) {
                        model.setStyle(cell, mxUtils.setStyle(model.getStyle(cell), 'image', getProviderLogo(task.chatModel?.provider)));
                    }

                    const vectorImage = vectorTaskImage(workflowTask, workflow.resources);
                    if (vectorImage) {
                        model.setStyle(cell, mxUtils.setStyle(model.getStyle(cell), 'image', vectorImage));
                    }

                    if (cell.isMap()) {
                        const itemProcessor = new mxCell({
                            mapTaskId: cell.id
                        });
                        itemProcessor.id = cell.getValue().itemProcessorId;
                        itemProcessor.setVisible(false);
                        workflowCell.insert(itemProcessor);
                    }
                }
            });

            const startWorkflowTask = model.getCell(child.startTask);
            if (startWorkflowTask && startWorkflowTask.id !== start.id &&
                !child.routes.some(route => route.source === start.id && route.target === startWorkflowTask.id)) {
                const edge = this.insertEdge(parent, uuid(), {
                    name: startTask.startRoute?.name,
                    routingType: 'UnConditional',
                }, start, startWorkflowTask);
                if (!isEmpty(startTask.startRoute?.geometryPoints)) {
                    edge.geometry.points = graphPoints(startTask.startRoute.geometryPoints);
                }
            }

            child.routes.forEach(route => {
                const {id, source, target, geometry, ...workflowRoute} = route;

                let sourceCell = model.getCell(source);
                const targetCell = model.getCell(target);

                if (sourceCell && targetCell) {
                    const edge = this.insertEdge(parent, id, workflowRoute, sourceCell, targetCell);
                    if (!isEmpty(geometry?.points)) {
                        edge.geometry.points = graphPoints(geometry.points);
                    }
                }
            });

            const hasAuthoredGeometry = startTask.geometry || child.tasks.some(task => task.geometry) ||
                !isEmpty(graphPoints(startTask.startRoute?.geometryPoints)) ||
                child.routes.some(route => !isEmpty(graphPoints(route.geometry?.points)));
            if (hasAuthoredGeometry) {
                autoLayout = false;
            } else if (child.generatedTerminalJoins?.length) {
                autoLayout = true;
            }
            if (autoLayout) {
                const hierarchicalLayout = new mxHierarchicalLayout(this,
                    mxConstants.DIRECTION_WEST);
                hierarchicalLayout.intraCellSpacing = 50;
                hierarchicalLayout.edgeStyle = 3;
                hierarchicalLayout.execute(this.getDefaultParent());
            }
            child.generatedTerminalJoins?.forEach(({joinId, branchCellIds}) => {
                const join = model.getCell(joinId);
                const branchCells = branchCellIds.map(id => model.getCell(id));
                const bounds = this.getBoundingBoxFromGeometry(branchCells, false);
                if (bounds) {
                    const geometry = join.geometry.clone();
                    const branchJoinGap = 50;
                    geometry.x = bounds.x + bounds.width + branchJoinGap;
                    geometry.y = bounds.y + (bounds.height - geometry.height) / 2;
                    model.setGeometry(join, geometry);
                }
            });
        });
    } finally {
        this.setDefaultParent(null);
        model.endUpdate();
    }
};

Graph.prototype.validate = function () {
    this.clearAllErrors();
    const model = this.getModel();
    const workflow = model.getRoot();
    const {name} = workflow.getValue();
    const children = model.getRoot().children;

    const validateContext = new ValidationContext();
    children.forEach(child => {
        const graphValidator = new GraphStructureValidator(name, child.getValue().startTask, child, model);
        graphValidator.validate(validateContext);
    });

    if (validateContext.hasError) {
        if (!isEmpty(validateContext.errors)) {
            this.setWorkflowErrors(validateContext.errors);
        }

        const taskErrors = {};
        validateContext.stepErrors.forEach(
            validationError => taskErrors[validationError.stepId] = {
                fields: {
                    ...validationError.fields,
                },
                message: validationError.message,
            });
        if (!isEmpty(taskErrors)) {
            this.setTaskErrors(taskErrors);
        }

        const routeErrors = {};
        validateContext.routeErrors.forEach(
            validationError => routeErrors[validationError.routeId] = {
                fields: {
                    ...validationError.fields,
                },
                message: validationError.message,
            });
        if (!isEmpty(routeErrors)) {
            this.setRouteErrors(routeErrors);
        }

        return false;
    }

    return true;
};

/**
 * Function: selectAll
 *
 * Selects all children of the given parent cell or the children of the
 * default parent if no parent is specified. To select leaf vertices and/or
 * edges use <selectCells>.
 *
 * Parameters:
 *
 * parent - Optional <mxCell> whose children should be selected.
 * Default is <defaultParent>.
 */
Graph.prototype.selectAll = function (parent) {
    parent = parent || this.getDefaultParent();

    if (!this.isCellLocked(parent)) {
        mxGraph.prototype.selectAll.apply(this, arguments);
    }
};

/**
 * Function: selectCells
 *
 * Selects all vertices and/or edges depending on the given boolean
 * arguments recursively, starting at the given parent or the default
 * parent if no parent is specified. Use <selectAll> to select all cells.
 * For vertices, only cells with no children are selected.
 *
 * Parameters:
 *
 * vertices - Boolean indicating if vertices should be selected.
 * edges - Boolean indicating if edges should be selected.
 * parent - Optional <mxCell> that acts as the root of the recursion.
 * Default is <defaultParent>.
 */
Graph.prototype.selectCells = function (vertices, edges, parent) {
    parent = parent || this.getDefaultParent();

    if (!this.isCellLocked(parent)) {
        mxGraph.prototype.selectCells.apply(this, arguments);
    }
};

Graph.prototype.removeOverlaysOfAllCells = function () {
    const parent = this.getDefaultParent();

    const cells = this.model.filterDescendants(cell => cell !== parent, parent);

    if (cells != null && cells.length > 0) {
        for (let i = 0; i < cells.length; i++) {
            if (cells[i] != null) {
                this.removeCellOverlays(cells[i]);
            }
        }
    }
};

Graph.prototype.getEdgeValidationError = function (edge, source, target) {
    if (source && target) {
        const tmp = this.model.getEdgesBetween(source, target, true);
        if (tmp.length > 1 || (tmp.length === 1 && tmp[0] !== edge)) {
            return '';
        }
    }

    return mxGraph.prototype.getEdgeValidationError.apply(this, arguments);
};

/**
 * Stops all interactions and clears the selection.
 */
Graph.prototype.reset = function () {
    if (this.isEditing()) {
        this.stopEditing(true);
    }

    this.escape();

    if (!this.isSelectionEmpty()) {
        this.clearSelection();
    }
};

/**
 * Overridden to limit zoom to 1% - 16.000%.
 */
Graph.prototype.zoom = function (factor, center) {
    factor = Math.max(0.01, Math.min(this.view.scale * factor, 160)) /
        this.view.scale;

    mxGraph.prototype.zoom.apply(this, arguments);
};

/**
 * Don't add overlay's image if already exists.
 */
Graph.prototype.addCellOverlay = function (cell, overlay) {
    if (cell == null) {
        return overlay;
    }

    cell.overlays = cell.overlays || [];
    const cellOverlays = cell.overlays;
    if (cellOverlays.some(
        cellOverlay => cellOverlay.image.src === overlay.image.src)) {
        return overlay;
    }

    return mxGraph.prototype.addCellOverlay.apply(this, arguments);
};

Graph.prototype.getCellOverlays = function (cell) {
    if (cell == null) {
        return [];
    }

    cell.overlays = cell.overlays || [];

    return mxGraph.prototype.getCellOverlays.apply(this, arguments) || [];
};

Graph.prototype.removeCellOverlay = function (cell, overlay) {
    if (cell == null) {
        return;
    }

    if (!overlay) {
        this.removeCellOverlays(cell);
    } else {
        cell.overlays = cell.overlays || [];
        const index = cell.overlays.findIndex(
            cellOverlay => cellOverlay.image.src === overlay.image.src);

        if (index >= 0) {
            cell.overlays.splice(index, 1);

            const state = this.view.getState(cell);

            if (state != null) {
                this.cellRenderer.redraw(state);
            }

            this.fireEvent(
                new mxEventObject(mxEvent.REMOVE_OVERLAY, 'cell', cell, 'overlay',
                    overlay));
        } else {
            overlay = null;
        }
    }

    return overlay;
};

Graph.prototype.errorOverlay = function (message, x = 10, y = 10) {
    return new mxCellOverlay(new mxImage(AlertCircleRedSvg, 24, 24), message,
        mxConstants.ALIGN_RIGHT, mxConstants.ALIGN_BOTTOM, new mxPoint(x, y));
};

Graph.prototype.forkOverlay = function (x = 0, y = -12) {
    return new mxCellOverlay(new mxImage(SetSplitOrangeSvg, 32, 32), null,
        mxConstants.ALIGN_RIGHT, mxConstants.ALIGN_TOP, new mxPoint(x, y),
        mxConstants.CURSOR_LABEL_HANDLE);
};

Graph.prototype.joinOverlay = function (x = 0, y = -12) {
    return new mxCellOverlay(new mxImage(SetMergeOrangeSvg, 32, 32), null,
        mxConstants.ALIGN_LEFT, mxConstants.ALIGN_TOP, new mxPoint(x, y),
        mxConstants.CURSOR_LABEL_HANDLE);
};

Graph.prototype.conditionsOverlay = function (x = 0, y = -17) {
    return new mxCellOverlay(new mxImage(CallSplitTealSvg, 18, 18), null,
        mxConstants.ALIGN_LEFT, mxConstants.ALIGN_TOP, new mxPoint(x, y),
        mxConstants.CURSOR_LABEL_HANDLE);
};

const createCreateExitMapButton = (cell, graph, parent, itemProcessor, parentViewport, onExitCallback) => {
    const container = graph.container;
    const graphBorderSizes = graph.getBorderSizes();

    const exitButton = document.createElement('button');
    exitButton.id = `exit-map-${itemProcessor.id}`;
    exitButton.className = 'ui mini basic grey button';
    exitButton.textContent = mxResources.get('subMapExit', [cell.getValue()?.name || 'Map Task']);
    exitButton.style.backgroundColor = '#fff';
    exitButton.style.position = 'absolute';
    exitButton.style.overflow = 'visible';
    exitButton.style.margin = '10px';
    exitButton.style.zIndex = '10';
    exitButton.style.top = (container.offsetTop + graphBorderSizes.y) + 'px';
    exitButton.style.left = (container.offsetLeft + graphBorderSizes.x) + 'px';
    exitButton.insertAdjacentHTML('afterbegin', '<i class="reply icon"></i>')

    container.parentNode.insertBefore(exitButton, container);

    mxEvent.addListener(exitButton, 'click', function (evt) {
        graph.flushLazyZoom();
        itemProcessor.setVisible(false);
        graph.setDefaultParent(parent);
        parent.setVisible(true);
        graph.updateSelection();
        graph.refresh();
        exitButton.remove();

        const parentButton = document.getElementById(`exit-map-${parent.id}`);
        if (parentButton) {
            parentButton.style.display = '';
        }

        onExitCallback(parent, parentViewport);

        mxEvent.consume(evt);
    });
}

Graph.prototype.mapOverlay = function (cell, itemProcessorId, onEnterCallback, onExitCallback) {
    const graph = this;
    const model = this.model;
    const overlay = new mxCellOverlay(new mxImage(ArrowDownCircleSvg, 28, 28), null,
        mxConstants.ALIGN_LEFT, mxConstants.ALIGN_BOTTOM, new mxPoint(-10, 10), 'pointer');

    overlay.addListener(mxEvent.CLICK, function (sender, evt) {
        graph.flushLazyZoom();
        const parentViewport = {
            scale: graph.view.scale,
            translateX: graph.view.translate.x,
            translateY: graph.view.translate.y,
            scrollLeft: graph.container.scrollLeft,
            scrollTop: graph.container.scrollTop,
        };
        const parent = graph.getDefaultParent();
        const exitButton = document.getElementById(`exit-map-${parent.id}`);
        if (exitButton) {
            exitButton.style.display = 'none';
        }

        const itemProcessor = model.getRoot().children.find(layer => layer.id === itemProcessorId);

        parent.setVisible(false);
        itemProcessor.setVisible(true);
        graph.setDefaultParent(itemProcessor);
        graph.updateSelection();
        graph.refresh();

        createCreateExitMapButton(cell, graph, parent, itemProcessor, parentViewport, onExitCallback);

        onEnterCallback(itemProcessor);
    });
    return overlay;
};

Graph.prototype.checkCircleOverlay = function (message = null, x = 10, y = 10) {
    return new mxCellOverlay(new mxImage(CheckCircleOutlineSvg, 20, 20), message,
        mxConstants.ALIGN_RIGHT, mxConstants.ALIGN_BOTTOM, new mxPoint(x, y),
        mxConstants.CURSOR_LABEL_HANDLE);
};

Graph.prototype.alertCircleOverlay = function (message = null, x = 10, y = 10) {
    return new mxCellOverlay(new mxImage(AlertCircleOutlineSvg, 20, 20), message,
        mxConstants.ALIGN_RIGHT, mxConstants.ALIGN_BOTTOM, new mxPoint(x, y),
        mxConstants.CURSOR_LABEL_HANDLE);
};

Graph.prototype.clockOverlay = function (message = null, x = 10, y = 10) {
    return new mxCellOverlay(new mxImage(ClockSvg, 20, 20), message,
        mxConstants.ALIGN_RIGHT, mxConstants.ALIGN_BOTTOM, new mxPoint(x, y),
        mxConstants.CURSOR_LABEL_HANDLE);
};

Graph.prototype.spinnerOverlay = function (message = null, x = 10, y = 10) {
    return new mxCellOverlay(new mxImage(SpinnerSvg, 18, 18), message,
        mxConstants.ALIGN_RIGHT, mxConstants.ALIGN_BOTTOM, new mxPoint(x, y),
        mxConstants.CURSOR_LABEL_HANDLE);
};

/**
 * Function: zoomIn
 *
 * Zooms into the graph by <zoomFactor>.
 */
Graph.prototype.zoomIn = function () {
    // Switches to 1% zoom steps below 15%
    if (this.view.scale < 0.15) {
        this.zoom((this.view.scale + 0.01) / this.view.scale);
    } else {
        // Uses to 5% zoom steps for better grid rendering in webkit
        // and to avoid rounding errors for zoom steps
        this.zoom((Math.round(this.view.scale * this.zoomFactor * 20) / 20) /
            this.view.scale);
    }
};

/**
 * Function: zoomOut
 *
 * Zooms out of the graph by <zoomFactor>.
 */
Graph.prototype.zoomOut = function () {
    // Switches to 1% zoom steps below 15%
    if (this.view.scale <= 0.15) {
        this.zoom((this.view.scale - 0.01) / this.view.scale);
    } else {
        // Uses to 5% zoom steps for better grid rendering in webkit
        // and to avoid rounding errors for zoom steps
        this.zoom((Math.round(this.view.scale * (1 / this.zoomFactor) * 20) / 20) /
            this.view.scale);
    }
};

/**
 * Overrides tooltips to show custom tooltip or metadata.
 */
Graph.prototype.getTooltipForCell = function (cell) {
    return '';
};

/**
 * Removes all illegal control characters with ASCII code <32 except TAB, LF
 * and CR.
 */
Graph.prototype.zapGremlins = function (text) {
    const checked = [];

    for (let i = 0; i < text.length; i++) {
        const code = text.charCodeAt(i);

        // Removes all control chars
        if (code >= 32) {
            checked.push(text.charAt(i));
        } else if (code === 9 || code === 10 || code === 13) {
            checked.push(' ');
        }
    }

    return checked.join('').trim();
};

Graph.prototype.clearCellErrorOverlays = function (cell) {
    cell = (cell != null) ? cell : this.model.getRoot();
    this.removeCellOverlay(cell, this.errorOverlay());

    const childCount = this.model.getChildCount(cell);

    for (let i = 0; i < childCount; i++) {
        const child = this.model.getChildAt(cell, i);
        this.clearCellErrorOverlays(child);
    }
};

/**
 * Hover icons are used for hover, vertex handler and drag from sidebar.
 */
export const HoverIcons = function (graph) {
    this.graph = graph;
    this.elts = [];
};

/**
 * Up arrow.
 */
HoverIcons.prototype.arrowSpacing = 6;

/**
 * Delay to switch to another state for overlapping bbox. Default is 500ms.
 */
HoverIcons.prototype.updateDelay = 500;

/**
 * Delay to switch between states. Default is 140ms.
 */
HoverIcons.prototype.activationDelay = 140;

/**
 * Up arrow.
 */
HoverIcons.prototype.currentState = null;

/**
 * Up arrow.
 */
HoverIcons.prototype.activeArrow = null;

/**
 * Up arrow.
 */
HoverIcons.prototype.inactiveOpacity = 15;

HoverIcons.prototype.cssCursor = 'pointer';

/**
 * Whether to hide arrows that collide with vertices.
 * LATER: Add keyboard override, touch support.
 */
HoverIcons.prototype.checkCollisions = true;
HoverIcons.prototype.triangleUp = createSvgImage(26, 14, `<path d="m 1 14 L 13 1 L 26 14 z" fill="#007dfc"/>`);
HoverIcons.prototype.triangleRight = createSvgImage(14, 26, '<path d="m 1 1 L 14 13 L 1 26 z" fill="#007dfc"/>');
HoverIcons.prototype.triangleDown = createSvgImage(26, 14, '<path d="m 1 1 L 13 14 L 26 1 z" fill="#007dfc"/>');
HoverIcons.prototype.triangleLeft = createSvgImage(14, 26, '<path d="m 14 1 L 1 13 L 14 26 z" fill="#007dfc"/>');
HoverIcons.prototype.roundDrop = createSvgImage(26, 26, '<circle cx="13" cy="13" r="12" fill="#007dfc"/>');
HoverIcons.prototype.refreshTarget = new mxImage(RefreshCircleSvg, 32, 32);
HoverIcons.prototype.tolerance = 0;

/**
 *
 */
HoverIcons.prototype.init = function () {
    this.arrowUp = this.createArrow(this.triangleUp, mxResources.get('plusTooltip'));
    this.arrowRight = this.createArrow(this.triangleRight, mxResources.get('plusTooltip'));
    this.arrowDown = this.createArrow(this.triangleDown, mxResources.get('plusTooltip'));
    this.arrowLeft = this.createArrow(this.triangleLeft, mxResources.get('plusTooltip'));

    this.elts = [this.arrowUp, this.arrowRight, this.arrowDown, this.arrowLeft];

    this.resetHandler = mxUtils.bind(this, function () {
        this.reset();
    });

    this.repaintHandler = mxUtils.bind(this, function () {
        this.repaint();
    });

    this.graph.selectionModel.addListener(mxEvent.CHANGE, this.repaintHandler);
    this.graph.model.addListener(mxEvent.CHANGE, this.repaintHandler);
    this.graph.view.addListener(mxEvent.SCALE_AND_TRANSLATE, this.repaintHandler);
    this.graph.view.addListener(mxEvent.TRANSLATE, this.repaintHandler);
    this.graph.view.addListener(mxEvent.SCALE, this.repaintHandler);
    this.graph.view.addListener(mxEvent.DOWN, this.repaintHandler);
    this.graph.view.addListener(mxEvent.UP, this.repaintHandler);
    this.graph.addListener(mxEvent.ROOT, this.repaintHandler);

    // Resets the mouse point on escape
    this.graph.addListener(mxEvent.ESCAPE, mxUtils.bind(this, function () {
        this.mouseDownPoint = null;
    }));

    // Removes hover icons if mouse leaves the container
    mxEvent.addListener(this.graph.container, 'mouseleave',
        mxUtils.bind(this, function (evt) {
            // Workaround for IE11 firing mouseleave for touch in diagram
            if (evt.relatedTarget != null && mxEvent.getSource(evt) ===
                this.graph.container) {
                this.setDisplay('none');
            }
        }));

    // Resets current state when in-place editor starts
    this.graph.addListener(mxEvent.START_EDITING,
        mxUtils.bind(this, function (evt) {
            this.reset();
        }));

    // Resets current state after update of selection state for touch events
    const graphClick = this.graph.click;
    this.graph.click = mxUtils.bind(this, function (me) {
        graphClick.apply(this.graph, arguments);

        if (this.currentState != null &&
            !this.graph.isCellSelected(this.currentState.cell) &&
            mxEvent.isTouchEvent(me.getEvent()) &&
            !this.graph.model.isVertex(me.getCell())) {
            this.reset();
        }
    });

    // Checks if connection handler was active in mouse move
    // as workaround for possible double connection inserted
    let connectionHandlerActive = false;

    // Implements a listener for hover and click handling
    this.graph.addMouseListener({
        mouseDown: mxUtils.bind(this, function (sender, me) {
            connectionHandlerActive = false;
            const evt = me.getEvent();

            if (this.isResetEvent(evt)) {
                this.reset();
            } else if (!this.isActive()) {
                const state = this.getState(me.getState());

                if (state != null || !mxEvent.isTouchEvent(evt)) {
                    this.update(state);
                }
            }

            this.setDisplay('none');
        }),
        mouseMove: mxUtils.bind(this, function (sender, me) {
            const evt = me.getEvent();

            if (this.isResetEvent(evt)) {
                this.reset();
            } else if (!this.graph.isMouseDown && !mxEvent.isTouchEvent(evt)) {
                this.update(this.getState(me.getState()), me.getGraphX(),
                    me.getGraphY());
            }

            if (this.graph.connectionHandler != null &&
                this.graph.connectionHandler.shape != null) {
                connectionHandlerActive = true;
            }
        }),
        mouseUp: mxUtils.bind(this, function (sender, me) {
            const evt = me.getEvent();

            if (this.isResetEvent(evt)) {
                this.reset();
            } else if (this.isActive()) {
                // Selects target vertex after drag and clone if not only new edge was inserted
                if (this.graph.getSelectionCount() !== 1 ||
                    !this.graph.model.isEdge(this.graph.getSelectionCell())) {
                    this.update(this.getState(this.graph.view.getState(
                        this.graph.getCellAt(me.getGraphX(), me.getGraphY()))));
                } else {
                    this.reset();
                }
            } else if (mxEvent.isTouchEvent(evt)) {
                const state = this.getTouchState(me);

                if (state != null && this.currentState !== state) {
                    this.removeNodes();
                    this.setCurrentState(state);
                }

                if (this.currentState != null) {
                    this.setDisplay('');
                    this.repaint();
                } else {
                    this.reset();
                }
            } else if (this.bbox != null &&
                mxUtils.contains(this.bbox, me.getGraphX(), me.getGraphY())) {
                // Shows existing hover icons if inside bounding box
                this.setDisplay('');
                this.repaint();
            } else if (!mxEvent.isTouchEvent(evt)) {
                this.reset();
            }

            connectionHandlerActive = false;
            this.resetActiveArrow();
        }),
    });
};

HoverIcons.prototype.isResetEvent = function (evt, allowShift) {
    return mxEvent.isAltDown(evt) ||
        (this.activeArrow == null && mxEvent.isShiftDown(evt)) ||
        mxEvent.isMetaDown(evt) ||
        (mxEvent.isPopupTrigger(evt) && !mxEvent.isControlDown(evt));
};

HoverIcons.prototype.createArrow = function (img, tooltip) {
    const arrow = mxUtils.createImage(img.src);
    arrow.style.width = img.width + 'px';
    arrow.style.height = img.height + 'px';
    arrow.style.padding = this.tolerance + 'px';

    if (tooltip != null && this.graph.tooltipHandler.isEnabled()) {
        arrow.setAttribute('title', tooltip);
    }

    arrow.style.position = 'absolute';
    arrow.style.cursor = this.cssCursor;
    arrow.style.pointerEvents = 'auto';
    arrow.style.zIndex = mxPopupMenu.prototype.zIndex;

    mxEvent.addGestureListeners(arrow, mxUtils.bind(this, function (evt) {
        if (this.currentState != null && !this.isResetEvent(evt)) {
            this.mouseDownPoint = mxUtils.convertPoint(this.graph.container,
                mxEvent.getClientX(evt), mxEvent.getClientY(evt));
            this.drag(evt, this.mouseDownPoint.x, this.mouseDownPoint.y);
            this.activeArrow = arrow;
            this.setDisplay('none');
            mxEvent.consume(evt);
        }
    }));

    mxEvent.redirectMouseEvents(arrow, this.graph, this.currentState);

    mxEvent.addListener(arrow, 'mouseenter', mxUtils.bind(this, function (evt) {
        if (mxEvent.isMouseEvent(evt)) {
            if (this.activeArrow != null && this.activeArrow !== arrow) {
                mxUtils.setOpacity(this.activeArrow, this.inactiveOpacity);
            }

            this.graph.connectionHandler.constraintHandler.reset();
            mxUtils.setOpacity(arrow, 100);
            this.activeArrow = arrow;
        }
    }));

    mxEvent.addListener(arrow, 'mouseleave', mxUtils.bind(this, function (evt) {
        if (!this.graph.isMouseDown) {
            this.resetActiveArrow();
        }
    }));

    return arrow;
};

HoverIcons.prototype.resetActiveArrow = function () {
    if (this.activeArrow != null) {
        mxUtils.setOpacity(this.activeArrow, this.inactiveOpacity);
        this.activeArrow = null;
    }
};

HoverIcons.prototype.getDirection = function () {
    let dir = mxConstants.DIRECTION_EAST;

    if (this.activeArrow === this.arrowUp) {
        dir = mxConstants.DIRECTION_NORTH;
    } else if (this.activeArrow === this.arrowDown) {
        dir = mxConstants.DIRECTION_SOUTH;
    } else if (this.activeArrow === this.arrowLeft) {
        dir = mxConstants.DIRECTION_WEST;
    }

    return dir;
};

HoverIcons.prototype.visitNodes = function (visitor) {
    for (let i = 0; i < this.elts.length; i++) {
        if (this.elts[i] != null) {
            visitor(this.elts[i]);
        }
    }
};

HoverIcons.prototype.removeNodes = function () {
    this.visitNodes(function (elt) {
        if (elt.parentNode != null) {
            elt.parentNode.removeChild(elt);
        }
    });
};

HoverIcons.prototype.setDisplay = function (display) {
    this.visitNodes(function (elt) {
        elt.style.display = display;
    });
};

HoverIcons.prototype.isActive = function () {
    return this.activeArrow != null && this.currentState != null;
};

HoverIcons.prototype.drag = function (evt, x, y) {
    this.graph.popupMenuHandler.hideMenu();
    this.graph.stopEditing(false);

    // Checks if state was removed in call to stopEditing above
    if (this.currentState != null) {
        this.graph.connectionHandler.start(this.currentState, x, y);
        this.graph.isMouseTrigger = mxEvent.isMouseEvent(evt);
        this.graph.isMouseDown = true;

        // Hides handles for selection cell
        const handler = this.graph.selectionCellsHandler.getHandler(
            this.currentState.cell);

        if (handler != null) {
            handler.setHandlesVisible(false);
        }
    }
};

HoverIcons.prototype.getStateAt = function (state, x, y) {
    return this.graph.view.getState(this.graph.getCellAt(x, y));
};

HoverIcons.prototype.click = function (state, dir, me) {
    const evt = me.getEvent();
    const x = me.getGraphX();
    const y = me.getGraphY();

    const tmp = this.getStateAt(state, x, y);

    if (tmp != null && this.graph.model.isEdge(tmp.cell) && !this.graph.isCloneEvent(evt) &&
        (tmp.getVisibleTerminalState(true) === state || tmp.getVisibleTerminalState(false) === state)) {
        this.graph.setSelectionCell(tmp.cell);
        this.reset();
    } else if (state != null) {
        this.execute(state, dir, me);
    }

    me.consume();
};

HoverIcons.prototype.execute = function (state, dir, me) {
    const evt = me.getEvent();

    this.graph.selectCellsForConnectVertex(this.graph.connectVertex(
        state.cell, dir, this.graph.defaultEdgeLength, evt, this.graph.isCloneEvent(evt),
        this.graph.isCloneEvent(evt)), evt, this);
};

HoverIcons.prototype.reset = function (clearTimeout) {
    clearTimeout = (clearTimeout == null) ? true : clearTimeout;

    if (clearTimeout && this.updateThread != null) {
        window.clearTimeout(this.updateThread);
    }

    this.mouseDownPoint = null;
    this.currentState = null;
    this.activeArrow = null;
    this.removeNodes();
    this.bbox = null;
};

HoverIcons.prototype.repaint = function () {
    this.bbox = null;

    if (this.currentState != null) {
        // Checks if cell was deleted
        this.currentState = this.getState(this.currentState);

        // Cell was deleted
        if (this.currentState != null &&
            this.graph.model.isVertex(this.currentState.cell) &&
            this.graph.isCellConnectable(this.currentState.cell)) {
            let bds = mxRectangle.fromRectangle(this.currentState);

            // Uses outer bounding box to take rotation into account
            if (this.currentState.shape != null &&
                this.currentState.shape.boundingBox != null) {
                bds = mxRectangle.fromRectangle(this.currentState.shape.boundingBox);
            }

            bds.grow(this.graph.tolerance);
            bds.grow(this.arrowSpacing);

            const positionArrow = mxUtils.bind(this, function (arrow, x, y) {
                arrow.style.left = x + 'px';
                arrow.style.top = y + 'px';
                mxUtils.setOpacity(arrow, this.inactiveOpacity);
            });

            positionArrow(this.arrowUp,
                Math.round(this.currentState.getCenterX() - this.triangleUp.width / 2 - this.tolerance),
                Math.round(bds.y - this.triangleUp.height - this.tolerance));

            positionArrow(this.arrowRight, Math.round(bds.x + bds.width - this.tolerance),
                Math.round(this.currentState.getCenterY() - this.triangleRight.height / 2 - this.tolerance));

            positionArrow(this.arrowDown, parseInt(this.arrowUp.style.left),
                Math.round(bds.y + bds.height - this.tolerance));

            positionArrow(this.arrowLeft, Math.round(bds.x - this.triangleLeft.width - this.tolerance),
                parseInt(this.arrowRight.style.top));

            if (this.checkCollisions) {
                let right = this.graph.getCellAt(bds.x + bds.width +
                    this.triangleRight.width / 2, this.currentState.getCenterY());
                let left = this.graph.getCellAt(bds.x - this.triangleLeft.width / 2, this.currentState.getCenterY());
                let top = this.graph.getCellAt(this.currentState.getCenterX(), bds.y - this.triangleUp.height / 2);
                let bottom = this.graph.getCellAt(this.currentState.getCenterX(), bds.y + bds.height + this.triangleDown.height / 2);

                // Shows hover icons large cell is behind all directions of current cell
                if (right != null && right === left && left === top && top === bottom) {
                    right = null;
                    left = null;
                    top = null;
                    bottom = null;
                }

                const currentGeo = this.graph.getCellGeometry(this.currentState.cell);

                const checkCollision = mxUtils.bind(this, function (cell, arrow) {
                    const geo = this.graph.model.isVertex(cell) && this.graph.getCellGeometry(cell);

                    // Ignores collision if vertex is more than 3 times the size of this vertex
                    if (cell != null && !this.graph.model.isAncestor(cell, this.currentState.cell) &&
                        (geo == null || currentGeo == null || (geo.height < 6 * currentGeo.height &&
                            geo.width < 6 * currentGeo.width))) {
                        arrow.style.visibility = 'hidden';
                    } else {
                        arrow.style.visibility = 'visible';
                    }
                });

                checkCollision(right, this.arrowRight);
                checkCollision(left, this.arrowLeft);
                checkCollision(top, this.arrowUp);
                checkCollision(bottom, this.arrowDown);
            } else {
                this.arrowLeft.style.visibility = 'visible';
                this.arrowRight.style.visibility = 'visible';
                this.arrowUp.style.visibility = 'visible';
                this.arrowDown.style.visibility = 'visible';
            }

            if (this.graph.tooltipHandler.isEnabled()) {
                this.arrowLeft.setAttribute('title', mxResources.get('plusTooltip'));
                this.arrowRight.setAttribute('title', mxResources.get('plusTooltip'));
                this.arrowUp.setAttribute('title', mxResources.get('plusTooltip'));
                this.arrowDown.setAttribute('title', mxResources.get('plusTooltip'));
            } else {
                this.arrowLeft.removeAttribute('title');
                this.arrowRight.removeAttribute('title');
                this.arrowUp.removeAttribute('title');
                this.arrowDown.removeAttribute('title');
            }
        } else {
            this.reset();
        }

        // Updates bounding box
        if (this.currentState != null) {
            this.bbox = this.computeBoundingBox();

            // Adds tolerance for hover
            if (this.bbox != null) {
                this.bbox.grow(10);
            }
        }
    }
};

HoverIcons.prototype.computeBoundingBox = function () {
    let bbox = (!this.graph.model.isEdge(this.currentState.cell)) ? mxRectangle.fromRectangle(this.currentState) : null;

    this.visitNodes(function (elt) {
        if (elt.parentNode != null) {
            const tmp = new mxRectangle(elt.offsetLeft, elt.offsetTop, elt.offsetWidth, elt.offsetHeight);

            if (bbox == null) {
                bbox = tmp;
            } else {
                bbox.add(tmp);
            }
        }
    });

    return bbox;
};

HoverIcons.prototype.getState = function (state) {
    if (state != null) {
        let cell = state.cell;

        // Uses connectable parent vertex if child is not connectable
        if (this.graph.getModel().isVertex(cell) &&
            !this.graph.isCellConnectable(cell)) {
            const parent = this.graph.getModel().getParent(cell);

            if (this.graph.getModel().isVertex(parent) &&
                this.graph.isCellConnectable(parent)) {
                cell = parent;
            }
        }

        // Ignores locked cells and edges
        if (this.graph.isCellLocked(cell) || this.graph.model.isEdge(cell)) {
            cell = null;
        }

        state = this.graph.view.getState(cell);
    }

    return state;
};

HoverIcons.prototype.getTouchState = function (me) {
    let state = me.getState() || me.sourceState ||
        this.graph.view.getState(this.graph.getCellAt(me.getGraphX(), me.getGraphY()));

    if (state == null) {
        const source = mxEvent.getSource(me.getEvent());
        state = this.graph.view.getCellStates()
            .find(cellState => this.graph.model.isVertex(cellState.cell) &&
                (cellState.shape?.node?.contains(source) ||
                    cellState.text?.node?.contains(source) ||
                    cellState.control?.node?.contains(source)));
    }

    return this.getState(state);
};

HoverIcons.prototype.update = function (state, x, y) {
    if (!this.graph.connectionArrowsEnabled) {
        this.reset();
    } else {
        let timeOnTarget = null;

        // Time on target
        if (this.prev !== state || this.isActive()) {
            this.startTime = new Date().getTime();
            this.prev = state;
            timeOnTarget = 0;

            if (this.updateThread != null) {
                window.clearTimeout(this.updateThread);
            }

            if (state != null) {
                // Starts timer to update current state with no mouse events
                this.updateThread = window.setTimeout(mxUtils.bind(this, function () {
                    if (!this.isActive() && !this.graph.isMouseDown &&
                        !this.graph.panningHandler.isActive()) {
                        this.prev = state;
                        this.update(state, x, y);
                    }
                }), this.updateDelay + 10);
            }
        } else if (this.startTime != null) {
            timeOnTarget = new Date().getTime() - this.startTime;
        }

        this.setDisplay('');

        if (this.currentState != null && this.currentState !== state &&
            timeOnTarget < this.activationDelay &&
            this.bbox != null && !mxUtils.contains(this.bbox, x, y)) {
            this.reset(false);
        } else if (this.currentState != null || timeOnTarget >
            this.activationDelay) {
            if (this.currentState !== state &&
                ((timeOnTarget > this.updateDelay && state != null) ||
                    this.bbox == null || x == null || y == null ||
                    !mxUtils.contains(this.bbox, x, y))) {
                if (state != null && this.graph.isEnabled()) {
                    this.removeNodes();
                    this.setCurrentState(state);
                    this.repaint();

                    // Resets connection points on other focused cells
                    if (this.graph.connectionHandler.constraintHandler.currentFocus !==
                        state) {
                        this.graph.connectionHandler.constraintHandler.reset();
                    }
                } else {
                    this.reset();
                }
            }
        }
    }
};

HoverIcons.prototype.setCurrentState = function (state) {
    if (state.style['portConstraint'] !== 'eastwest') {
        this.graph.container.appendChild(this.arrowUp);
        this.graph.container.appendChild(this.arrowDown);
    }

    this.graph.container.appendChild(this.arrowRight);
    this.graph.container.appendChild(this.arrowLeft);
    this.currentState = state;
};

(function () {
    const mxGraphViewResetValidationState = mxGraphView.prototype.resetValidationState;

    mxGraphView.prototype.resetValidationState = function () {
        mxGraphViewResetValidationState.apply(this, arguments);
        this.validEdges = [];
    };

    const mxGraphViewValidateCellState = mxGraphView.prototype.validateCellState;

    mxGraphView.prototype.validateCellState = function (cell, recurse) {
        let state = this.getState(cell);

        state = mxGraphViewValidateCellState.apply(this, arguments);

        // Adds to the list of edges that may intersect with later edges
        if (state != null && this.graph.model.isEdge(state.cell) &&
            state.style[mxConstants.STYLE_CURVED] !== 1) {
            // LATER: Reuse jumps for valid edges
            this.validEdges.push(state);
        }

        return state;
    };

    const mxCellRendererIsShapeInvalid = mxCellRenderer.prototype.isShapeInvalid;

    mxCellRenderer.prototype.isShapeInvalid = function (state, shape) {
        return mxCellRendererIsShapeInvalid.apply(this, arguments) ||
            (state.routedPoints != null && shape.routedPoints != null &&
                !mxUtils.equalPoints(shape.routedPoints, state.routedPoints));
    };

    /**
     * Adds support for snapToPoint style.
     */
    const mxGraphViewUpdateFloatingTerminalPoint = mxGraphView.prototype.updateFloatingTerminalPoint;

    mxGraphView.prototype.updateFloatingTerminalPoint = function (
        edge, start, end, source) {
        if (start != null && edge != null &&
            (isStyleEnabled(start.style['snapToPoint']) ||
                isStyleEnabled(edge.style['snapToPoint']))) {
            start = this.getTerminalPort(edge, start, source);
            let next = this.getNextPoint(edge, end, source);

            const orth = this.graph.isOrthogonal(edge);
            const alpha = mxUtils.toRadians(
                Number(start.style[mxConstants.STYLE_ROTATION] || '0'));
            const center = new mxPoint(start.getCenterX(), start.getCenterY());

            if (alpha !== 0) {
                const cos = Math.cos(-alpha);
                const sin = Math.sin(-alpha);
                next = mxUtils.getRotatedPoint(next, cos, sin, center);
            }

            let border = parseFloat(
                edge.style[mxConstants.STYLE_PERIMETER_SPACING] || 0);
            border += parseFloat(edge.style[(source) ?
                mxConstants.STYLE_SOURCE_PERIMETER_SPACING :
                mxConstants.STYLE_TARGET_PERIMETER_SPACING] || 0);
            let pt = this.getPerimeterPoint(start, next, alpha === 0 && orth, border);

            if (alpha !== 0) {
                const cos = Math.cos(alpha);
                const sin = Math.sin(alpha);
                pt = mxUtils.getRotatedPoint(pt, cos, sin, center);
            }

            edge.setAbsoluteTerminalPoint(
                this.snapToAnchorPoint(edge, start, end, source, pt), source);
        } else {
            mxGraphViewUpdateFloatingTerminalPoint.apply(this, arguments);
        }
    };

    mxGraphView.prototype.snapToAnchorPoint = function (
        edge, start, end, source, pt) {
        if (start != null && edge != null) {
            const constraints = this.graph.getAllConnectionConstraints(start);
            let nearest = null;
            let dist = null;

            if (constraints != null) {
                for (let i = 0; i < constraints.length; i++) {
                    const cp = this.graph.getConnectionPoint(start, constraints[i]);

                    if (cp != null) {
                        const tmp = (cp.x - pt.x) * (cp.x - pt.x) + (cp.y - pt.y) *
                            (cp.y - pt.y);

                        if (dist == null || tmp < dist) {
                            nearest = cp;
                            dist = tmp;
                        }
                    }
                }
            }

            if (nearest != null) {
                pt = nearest;
            }
        }

        return pt;
    };
})();

(function () {
    // Sets colors for handles
    mxConstants.HANDLE_FILLCOLOR = '#99ccff';
    mxConstants.HANDLE_STROKECOLOR = '#0088cf';
    mxConstants.VERTEX_SELECTION_COLOR = '#00a8ff';
    mxConstants.OUTLINE_COLOR = '#00a8ff';
    mxConstants.OUTLINE_HANDLE_FILLCOLOR = '#99ccff';
    mxConstants.OUTLINE_HANDLE_STROKECOLOR = '#00a8ff';
    mxConstants.CONNECT_HANDLE_FILLCOLOR = '#cee7ff';
    mxConstants.EDGE_SELECTION_COLOR = '#00a8ff';
    mxConstants.DEFAULT_VALID_COLOR = '#00a8ff';
    mxConstants.LABEL_HANDLE_FILLCOLOR = '#cee7ff';
    mxConstants.GUIDE_COLOR = '#0088cf';
    mxConstants.HIGHLIGHT_OPACITY = 30;
    mxConstants.HIGHLIGHT_SIZE = 8;

    // Enables snapping to off-grid terminals for edge waypoints
    mxEdgeHandler.prototype.snapToTerminals = true;

    // Enables guides
    mxGraphHandler.prototype.guidesEnabled = true;

    // Enables fading of rubberband
    mxRubberband.prototype.fadeOut = true;

    // Alt-move disables guides
    mxGuide.prototype.isEnabledForEvent = function (evt) {
        return !mxEvent.isAltDown(evt);
    };

    // Overrides highlight shape for connection points
    mxConstraintHandler.prototype.createHighlightShape = function () {
        const hl = new mxEllipse(null, this.highlightColor, this.highlightColor, 0);
        hl.opacity = mxConstants.HIGHLIGHT_OPACITY;

        return hl;
    };

    // Overrides edge preview to use current edge shape and default style
    mxConnectionHandler.prototype.livePreview = true;
    mxConnectionHandler.prototype.cursor = 'crosshair';

    // Uses current edge style for connect preview
    mxConnectionHandler.prototype.createEdgeState = function (me) {
        let style = this.graph.createCurrentEdgeStyle();
        style = mxUtils.setStyle(style, 'shape', 'connector');
        style = mxUtils.setStyle(style, 'endArrow', 'classic');
        style = mxUtils.setStyle(style, 'endFill', true);
        style = mxUtils.setStyle(style, 'strokeWidth',
            this.graph.currentEdgeStyle?.strokeWidth || this.graph.defaultEdgeStyle?.strokeWidth || 1);
        const edge = this.graph.createEdge(null, null, null, null, null, style);
        const state = new mxCellState(this.graph.view, edge,
            this.graph.getCellStyle(edge));

        for (let key in this.graph.currentEdgeStyle) {
            state.style[key] = this.graph.currentEdgeStyle[key];
        }

        return state;
    };

    // Overrides dashed state with current edge style
    const applyRoutePreviewMarkerStyle = (shape, state) => {
        if (!shape) {
            return;
        }

        const style = state?.style || shape.style || {};
        style.shape = style.shape || 'connector';
        style.endArrow = style.endArrow || 'classic';
        style.endFill = style.endFill == null ? true : style.endFill;
        style.strokeWidth = style.strokeWidth || 1;

        shape.outline = false;
        shape.style = style;
        shape.endArrow = style.endArrow;
        shape.endFill = style.endFill;
        shape.strokeWidth = style.strokeWidth;
    };

    const connectionHandlerCreateShape = mxConnectionHandler.prototype.createShape;
    mxConnectionHandler.prototype.createShape = function () {
        const shape = connectionHandlerCreateShape.apply(this, arguments);

        applyRoutePreviewMarkerStyle(shape, this.edgeState);
        shape.isDashed = this.graph.currentEdgeStyle[mxConstants.STYLE_DASHED] ===
            '1';

        return shape;
    };

    const connectionHandlerUpdatePreview = mxConnectionHandler.prototype.updatePreview;
    mxConnectionHandler.prototype.updatePreview = function (valid) {
        connectionHandlerUpdatePreview.apply(this, arguments);
        applyRoutePreviewMarkerStyle(this.shape, this.edgeState);

        if (this.shape != null && this.edgeState?.style != null) {
            this.shape.stroke = this.edgeState.style.strokeColor || this.shape.stroke;
            this.shape.strokeWidth = this.edgeState.style.strokeWidth || 1;
        }
    };

    const connectionHandlerDrawPreview = mxConnectionHandler.prototype.drawPreview;
    mxConnectionHandler.prototype.drawPreview = function () {
        applyRoutePreviewMarkerStyle(this.shape, this.edgeState);
        connectionHandlerDrawPreview.apply(this, arguments);
        applyRoutePreviewMarkerStyle(this.shape, this.edgeState);
        this.shape?.redraw?.();
    };

    const connectionHandlerCheckConstraints = mxConnectionHandler.prototype.checkConstraints;
    mxConnectionHandler.prototype.checkConstraints = function (sourceConstraint, targetConstraint) {
        if (this.graph.isAllowLoops() && this.previous?.cell != null &&
            this.currentState?.cell === this.previous.cell) {
            return true;
        }

        return connectionHandlerCheckConstraints.apply(this, arguments);
    };

    const connectionHandlerSelectCells = mxConnectionHandler.prototype.selectCells;
    mxConnectionHandler.prototype.selectCells = function (edge, target) {
        this.graph?.clearSelfLoopConnectionConstraints?.(edge);
        const result = connectionHandlerSelectCells.apply(this, arguments);
        this.graph?.clearSelfLoopConnectionConstraints?.(edge);

        return result;
    };

    const connectionConstraintStyleKeys = source => source ?
        ['exitX', 'exitY', 'exitDx', 'exitDy', 'exitPerimeter'] :
        ['entryX', 'entryY', 'entryDx', 'entryDy', 'entryPerimeter'];

    const clearConnectionStateConstraintStyle = (style, source) => {
        connectionConstraintStyleKeys(source).forEach(key => delete style[key]);
    };

    const setConnectionStateConstraintStyle = (style, source, constraint) => {
        if (!constraint?.point) {
            clearConnectionStateConstraintStyle(style, source);
            return;
        }

        style[source ? 'exitX' : 'entryX'] = constraint.point.x;
        style[source ? 'exitY' : 'entryY'] = constraint.point.y;
    };

    mxConnectionHandler.prototype.updateEdgeState = function (current, constraint) {
        if (!this.edgeState) {
            return;
        }

        const selfLoop = this.previous?.cell != null &&
            this.previous.cell === this.currentState?.cell;

        if (selfLoop) {
            clearConnectionStateConstraintStyle(this.edgeState.style, true);
            clearConnectionStateConstraintStyle(this.edgeState.style, false);
            this.edgeState.absolutePoints = [null, null];
        } else {
            setConnectionStateConstraintStyle(this.edgeState.style, true, this.sourceConstraint);
            setConnectionStateConstraintStyle(this.edgeState.style, false, constraint);

            this.edgeState.absolutePoints = [null, this.currentState != null ? null : current];
            if (this.sourceConstraint) {
                this.graph.view.updateFixedTerminalPoint(this.edgeState, this.previous, true, this.sourceConstraint);
            }
            if (this.currentState != null) {
                if (constraint == null) {
                    constraint = this.graph.getConnectionConstraint(this.edgeState, this.previous, false);
                }
                this.edgeState.setAbsoluteTerminalPoint(null, false);
                this.graph.view.updateFixedTerminalPoint(this.edgeState, this.currentState, false, constraint);
            }
        }

        const realPoints = [];
        for (let i = 0; i < this.waypoints.length; i++) {
            const point = this.waypoints[i].clone();
            this.convertWaypoint(point);
            realPoints[i] = point;
        }

        this.graph.view.updatePoints(this.edgeState, realPoints, this.previous, this.currentState);
        this.graph.view.updateFloatingTerminalPoints(this.edgeState, this.previous, this.currentState);
    };

    // Overrides connection handler to ignore edges instead of not allowing connections
    const mxConnectionHandlerCreateMarker = mxConnectionHandler.prototype.createMarker;
    mxConnectionHandler.prototype.createMarker = function () {
        const marker = mxConnectionHandlerCreateMarker.apply(this, arguments);
        const connectionHandler = this;

        const markerGetCell = marker.getCell;
        marker.getCell = function (me) {
            const result = markerGetCell.apply(marker, arguments);

            connectionHandler.error = null;

            return result;
        };

        return marker;
    };

    mxConnectionHandler.prototype.isCellEnabled = function (cell) {
        return !this.graph.isCellLocked(cell);
    };

    mxConnectionHandler.prototype.isConnectableCell = function (cell) {
        return this.graph.getModel().isVertex(cell);
    };

    Graph.prototype.defaultVertexStyle = defaultStyles.defaultVertex;
    Graph.prototype.defaultEdgeStyle = defaultStyles.defaultEdge;

    const currentEdgeStyleKeys = [
        'edgeStyle',
        'shape',
        'curved',
        'rounded',
        'comic',
        'jumpStyle',
        'jumpSize',
        'startArrow',
        'startFill',
        'startSize',
        'endArrow',
        'endFill',
        'endSize',
    ];

    /**
     * Returns the current edge style as a string.
     */
    Graph.prototype.createCurrentEdgeStyle = function () {
        const style = currentEdgeStyleKeys
            .map(key => [key, this.currentEdgeStyle[key]])
            .filter(([, value]) => value != null)
            .map(([key, value]) => `${key}=${value};`)
            .join('');

        // Special logic for custom property of elbowEdgeStyle
        if (this.currentEdgeStyle['edgeStyle'] === 'elbowEdgeStyle' &&
            this.currentEdgeStyle['elbow'] != null) {
            return style + 'elbow=' + this.currentEdgeStyle['elbow'] + ';';
        }

        return style;
    };

    /**
     * Hook for subclassers.
     */
    Graph.prototype.getPagePadding = function () {
        return new mxPoint(0, 0);
    };

    /**
     * Overrides method to provide connection constraints for shapes.
     */
    Graph.prototype.getAllConnectionConstraints = function (terminal, source) {
        if (terminal != null) {
            const constraints = mxUtils.getValue(terminal.style, 'points', null);

            if (constraints != null) {
                // Requires an array of arrays with x, y (0..1) and an optional
                // perimeter (0 or 1), eg. points=[[0,0,1],[0,1,0],[1,1]]
                const result = [];

                try {
                    const c = parseJSON(constraints) || [];

                    for (let i = 0; i < c.length; i++) {
                        const tmp = c[i];
                        result.push(new mxConnectionConstraint(new mxPoint(tmp[0], tmp[1]),
                            (tmp.length > 2) ? tmp[2] !== '0' : true));
                    }
                } catch (e) {
                    // ignore
                }

                return result;
            } else {
                if (terminal.shape != null) {
                    if (terminal.shape.stencil != null) {
                        return terminal.shape.stencil.constraints;
                    } else if (terminal.shape.constraints != null) {
                        return terminal.shape.constraints;
                    }
                }
            }
        }

        return null;
    };

    /**
     * Inverts the elbow edge style without removing existing styles.
     */
    Graph.prototype.flipEdge = function (edge) {
        if (edge != null) {
            const state = this.view.getState(edge);
            const style = (state != null) ? state.style : this.getCellStyle(edge);

            if (style != null) {
                const elbow = mxUtils.getValue(style, mxConstants.STYLE_ELBOW,
                    mxConstants.ELBOW_HORIZONTAL);
                const value = (elbow === mxConstants.ELBOW_HORIZONTAL) ?
                    mxConstants.ELBOW_VERTICAL : mxConstants.ELBOW_HORIZONTAL;
                this.setCellStyles(mxConstants.STYLE_ELBOW, value, [edge]);
            }
        }
    };

    /**
     * Overrides autosize to add a border.
     */
    Graph.prototype.getPreferredSizeForCell = function (cell) {
        const result = mxGraph.prototype.getPreferredSizeForCell.apply(this,
            arguments);

        // Adds buffer
        if (result != null) {
            result.width += 10;
            result.height += 4;

            if (this.gridEnabled) {
                result.width = this.snap(result.width);
                result.height = this.snap(result.height);
            }
        }

        return result;
    };

    /**
     * Replaces the given element with a span.
     */
    Graph.prototype.replaceElement = function (elt, tagName) {
        const span = elt.ownerDocument.createElement(
            (tagName != null) ? tagName : 'span');
        const attributes = Array.prototype.slice.call(elt.attributes);

        let attr;
        while ((attr = attributes.pop())) {
            span.setAttribute(attr.nodeName, attr.nodeValue);
        }

        span.innerHTML = elt.innerHTML;
        elt.parentNode.replaceChild(span, elt);
    };

    /**
     * Handles label changes for XML user objects.
     */
    Graph.prototype.cellLabelChanged = function (cell, value, autoSize) {
        value = this.zapGremlins(value);

        const cellValue = deepCopy(cell.getValue());
        if (cellValue != null && cellValue.hasOwnProperty('name')) {
            const newValue = {
                ...cellValue,
                name: value,
            };
            mxGraph.prototype.cellLabelChanged.call(this, cell, newValue, autoSize);
        } else {
            mxGraph.prototype.cellLabelChanged.call(this, cell, value, autoSize);
        }
    };

    /**
     * Removes transparent empty groups if all children are removed.
     */
    Graph.prototype.cellsRemoved = function (cells) {
        if (cells != null) {
            const dict = new mxDictionary();

            for (let i = 0; i < cells.length; i++) {
                dict.put(cells[i], true);
            }

            // LATER: Recurse up the cell hierarchy
            const parents = [];

            for (let i = 0; i < cells.length; i++) {
                const parent = this.model.getParent(cells[i]);

                if (parent != null && !dict.get(parent)) {
                    dict.put(parent, true);
                    parents.push(parent);
                }
            }

            for (let i = 0; i < parents.length; i++) {
                const state = this.view.getState(parents[i]);

                if (state != null && (this.model.isEdge(state.cell) ||
                        this.model.isVertex(state.cell)) &&
                    this.isCellDeletable(state.cell)) {
                    const stroke = mxUtils.getValue(state.style,
                        mxConstants.STYLE_STROKECOLOR, mxConstants.NONE);
                    const fill = mxUtils.getValue(state.style,
                        mxConstants.STYLE_FILLCOLOR, mxConstants.NONE);

                    if (stroke === mxConstants.NONE && fill === mxConstants.NONE) {
                        let allChildren = true;

                        for (let j = 0; j < this.model.getChildCount(state.cell) &&
                        allChildren; j++) {
                            if (!dict.get(this.model.getChildAt(state.cell, j))) {
                                allChildren = false;
                            }
                        }

                        if (allChildren) {
                            cells.push(state.cell);
                        }
                    }
                }
            }
        }

        mxGraph.prototype.cellsRemoved.apply(this, arguments);
    };

    /**
     * Overridden to stop moving edge labels between cells.
     */
    Graph.prototype.getDropTarget = function (cells, evt, cell, clone) {
        // Disables drop into group if alt is pressed
        if (mxEvent.isAltDown(evt)) {
            return null;
        }

        // Disables dragging edge labels out of edges
        for (let i = 0; i < cells.length; i++) {
            if (this.model.isEdge(this.model.getParent(cells[i]))) {
                return null;
            }
        }

        return mxGraph.prototype.getDropTarget.apply(this, arguments);
    };

    /**
     * Returns a point that specifies the location for inserting cells.
     */
    Graph.prototype.getInsertPoint = function () {
        const gs = this.getGridSize();
        let dx = this.container.scrollLeft / this.view.scale -
            this.view.translate.x;
        let dy = this.container.scrollTop / this.view.scale - this.view.translate.y;

        return new mxPoint(this.snap(dx + gs), this.snap(dy + gs));
    };

    /**
     *
     */
    Graph.prototype.getFreeInsertPoint = function () {
        const view = this.view;
        const bds = this.getGraphBounds();
        const pt = this.getInsertPoint();

        // Places at same x-coord and 2 grid sizes below existing graph
        const x = this.snap(
            Math.round(Math.max(pt.x, bds.x / view.scale - view.translate.x +
                ((bds.width === 0) ? 2 * this.gridSize : 0))));
        const y = this.snap(Math.round(
            Math.max(pt.y, (bds.y + bds.height) / view.scale - view.translate.y +
                2 * this.gridSize)));

        return new mxPoint(x, y);
    };

    /**
     * Hook for subclassers to return true if the current insert point was defined
     * using a mouse hover event.
     */
    Graph.prototype.isMouseInsertPoint = function () {
        return false;
    };

    /**
     * Adds a handler for clicking on shapes with links. This replaces all links in labels.
     */
    Graph.prototype.addClickHandler = function (highlight, beforeClick, onClick) {
        const cursor = this.container.style.cursor;
        const tol = this.getTolerance();
        const graph = this;

        const mouseListener = {
            currentState: null,
            highlight: (highlight != null && highlight !== '' && highlight !==
                mxConstants.NONE) ?
                new mxCellHighlight(graph, highlight, 4) :
                null,
            startX: 0,
            startY: 0,
            scrollLeft: 0,
            scrollTop: 0,
            updateCurrentState: function (me) {
                let tmp = me.sourceState;
                if (tmp !== this.currentState) {
                    if (this.currentState != null) {
                        this.clear();
                    }

                    this.currentState = tmp;

                    if (this.currentState != null) {
                        this.activate(this.currentState);
                    }
                }
            },
            mouseDown: function (sender, me) {
                this.startX = me.getGraphX();
                this.startY = me.getGraphY();
                this.scrollLeft = graph.container.scrollLeft;
                this.scrollTop = graph.container.scrollTop;

                if (graph.container.style.overflow === 'auto') {
                    graph.container.style.cursor = 'move';
                }

                this.updateCurrentState(me);
            },
            mouseMove: function (sender, me) {
                if (!graph.isMouseDown) {
                    if (this.currentState != null &&
                        (me.getState() === this.currentState || me.sourceState ==
                            null) &&
                        graph.intersects(this.currentState, me.getGraphX(),
                            me.getGraphY())) {
                        return;
                    }

                    this.updateCurrentState(me);
                }
            },
            mouseUp: function (sender, me) {
                const evt = me.getEvent();

                // Ignores clicks on links and collapse/expand icon
                if (((Math.abs(this.scrollLeft - graph.container.scrollLeft) <
                            tol &&
                            Math.abs(this.scrollTop - graph.container.scrollTop) <
                            tol) &&
                        (me.sourceState == null ||
                            !me.isSource(me.sourceState.control))) &&
                    (((mxEvent.isLeftMouseButton(evt) ||
                                mxEvent.isMiddleMouseButton(evt)) &&
                            !mxEvent.isPopupTrigger(evt)) ||
                        mxEvent.isTouchEvent(evt))) {
                    if (onClick != null && !me.isConsumed() &&
                        (Math.abs(this.scrollLeft - graph.container.scrollLeft) <
                            tol &&
                            Math.abs(this.scrollTop - graph.container.scrollTop) <
                            tol) &&
                        (Math.abs(this.startX - me.getGraphX()) < tol &&
                            Math.abs(this.startY - me.getGraphY()) < tol)) {
                        onClick(me.getEvent());
                    }
                }

                this.clear();
            },
            clear: function () {
                if (graph.container != null) {
                    graph.container.style.cursor = cursor;
                }

                this.currentState = null;

                if (this.highlight != null) {
                    this.highlight.hide();
                }

                if (graph.tooltipHandler != null) {
                    graph.tooltipHandler.hide();
                }
            },
        };

        // Ignores built-in click handling
        graph.click = function (me) {
        };
        graph.addMouseListener(mouseListener);

        mxEvent.addListener(document, 'mouseleave', function (evt) {
            mouseListener.clear();
        });
    };

    /**
     * Duplicates the given cells and returns the duplicates.
     */
    Graph.prototype.duplicateCells = function (cells, append) {
        cells = (cells != null) ? cells : this.getSelectionCells();
        append = (append != null) ? append : true;

        cells = this.model.getTopmostCells(cells);

        const model = this.getModel();
        const s = this.gridSize;
        const select = [];

        model.beginUpdate();
        try {
            const clones = this.cloneCells(cells, false);

            for (let i = 0; i < cells.length; i++) {
                if (cells[i].isStart()) {
                    continue;
                }

                const parent = model.getParent(cells[i]);
                const child = this.moveCells([clones[i]], s, s, false)[0];
                select.push(child);

                if (append) {
                    model.add(parent, clones[i]);
                } else {
                    // Maintains child index by inserting after clone in parent
                    const index = parent.getIndex(cells[i]);
                    model.add(parent, clones[i], index + 1);
                }
            }
        } finally {
            model.endUpdate();
        }

        return select;
    };

    /**
     *
     * @param cell
     * @returns {Boolean}
     */
    Graph.prototype.isCellResizable = function (cell) {
        const result = mxGraph.prototype.isCellResizable.apply(this, arguments);

        const state = this.view.getState(cell);
        const style = (state != null) ? state.style : this.getCellStyle(cell);

        return result ||
            (!isStyleDisabled(mxUtils.getValue(style, mxConstants.STYLE_RESIZABLE, '1')) &&
                style[mxConstants.STYLE_WHITE_SPACE] === 'wrap');
    };

    /**
     * Function: distributeCells
     *
     * Distribuets the centers of the given cells equally along the available
     * horizontal or vertical space.
     *
     * Parameters:
     *
     * horizontal - Boolean that specifies the direction of the distribution.
     * cells - Optional array of <mxCells> to be distributed. Edges are ignored.
     */
    Graph.prototype.distributeCells = function (horizontal, cells) {
        if (cells == null) {
            cells = this.getSelectionCells();
        }

        if (cells != null && cells.length > 1) {
            const vertices = [];
            let max = null;
            let min = null;

            for (let i = 0; i < cells.length; i++) {
                if (this.getModel().isVertex(cells[i])) {
                    const state = this.view.getState(cells[i]);

                    if (state != null) {
                        const tmp = (horizontal) ? state.getCenterX() : state.getCenterY();
                        max = (max != null) ? Math.max(max, tmp) : tmp;
                        min = (min != null) ? Math.min(min, tmp) : tmp;

                        vertices.push(state);
                    }
                }
            }

            if (vertices.length > 2) {
                vertices.sort(function (a, b) {
                    return (horizontal) ? a.x - b.x : a.y - b.y;
                });

                const t = this.view.translate;
                const s = this.view.scale;

                min = min / s - ((horizontal) ? t.x : t.y);
                max = max / s - ((horizontal) ? t.x : t.y);

                this.getModel().beginUpdate();
                try {
                    const dt = (max - min) / (vertices.length - 1);
                    let t0 = min;

                    for (let i = 1; i < vertices.length - 1; i++) {
                        const pstate = this.view.getState(
                            this.model.getParent(vertices[i].cell));
                        let geo = this.getCellGeometry(vertices[i].cell);
                        t0 += dt;

                        if (geo != null && pstate != null) {
                            geo = geo.clone();

                            if (horizontal) {
                                geo.x = Math.round(t0 - geo.width / 2) - pstate.origin.x;
                            } else {
                                geo.y = Math.round(t0 - geo.height / 2) - pstate.origin.y;
                            }

                            this.getModel().setGeometry(vertices[i].cell, geo);
                        }
                    }
                } finally {
                    this.getModel().endUpdate();
                }
            }
        }

        return cells;
    };

    /**
     * Adds meta-drag an Mac.
     * @param evt
     * @returns
     */
    Graph.prototype.isCloneEvent = function (evt) {
        return (mxClient.IS_MAC && mxEvent.isMetaDown(evt)) ||
            mxEvent.isControlDown(evt);
    };

    /**
     * Hook for creating the canvas used in getSvg.
     */
    Graph.prototype.createSvgCanvas = function (node) {
        return new mxSvgCanvas2D(node);
    };

    /**
     * Returns the first ancestor of the current selection with the given name.
     */
    Graph.prototype.getSelectedElement = function () {
        let node = null;

        if (window.getSelection) {
            const sel = window.getSelection();

            if (sel.getRangeAt && sel.rangeCount) {
                const range = sel.getRangeAt(0);
                node = range.commonAncestorContainer;
            }
        } else if (document.selection) {
            node = document.selection.createRange().parentElement();
        }

        return node;
    };

    /**
     * Returns the first ancestor of the current selection with the given name.
     */
    Graph.prototype.getParentByName = function (node, name, stopAt) {
        while (node != null) {
            if (node.nodeName === name) {
                return node;
            }

            if (node === stopAt) {
                return null;
            }

            node = node.parentNode;
        }

        return node;
    };

    /**
     * Selects the given node.
     */
    Graph.prototype.selectNode = function (node) {
        let sel = null;

        // IE9 and non-IE
        if (window.getSelection) {
            sel = window.getSelection();

            if (sel.getRangeAt && sel.rangeCount) {
                const range = document.createRange();
                range.selectNode(node);
                sel.removeAllRanges();
                sel.addRange(range);
            }
        }
        // IE < 9
        else if ((sel = document.selection) && sel.type !== 'Control') {
            const originalRange = sel.createRange();
            originalRange.collapse(true);
            const range = sel.createRange();
            range.setEndPoint('StartToStart', originalRange);
            range.select();
        }
    };

    /**
     * Customized graph for touch devices.
     */
    Graph.prototype.initTouch = function () {
        // Disables new connections via "hotspot"
        this.connectionHandler.marker.isEnabled = function () {
            return this.graph.connectionHandler.first != null;
        };

        // Hides menu when editing starts
        this.addListener(mxEvent.START_EDITING, function (sender, evt) {
            this.popupMenuHandler.hideMenu();
        });

        // Adds custom hit detection if native hit detection found no cell
        const graphUpdateMouseEvent = this.updateMouseEvent;
        this.updateMouseEvent = function (me) {
            me = graphUpdateMouseEvent.apply(this, arguments);

            if (mxEvent.isTouchEvent(me.getEvent()) && me.getState() == null) {
                let cell = this.getCellAt(me.graphX, me.graphY);
                const eventSourceState = cell == null ?
                    getStateForEventSource(this, me.getEvent()) :
                    null;

                if (eventSourceState != null) {
                    me.state = eventSourceState;
                    this.container.style.cursor = me.state.shape?.node?.style?.cursor || '';
                } else if (cell != null && this.isSwimlane(cell) &&
                    this.hitsSwimlaneContent(cell, me.graphX, me.graphY)) {
                    cell = null;
                } else {
                    me.state = this.view.getState(cell);

                    if (me.state != null && me.state.shape != null) {
                        this.container.style.cursor = me.state.shape.node.style.cursor;
                    }
                }
            }

            if (me.getState() == null && this.isEnabled()) {
                this.container.style.cursor = 'default';
            }

            return me;
        };

        let cellSelected = false;
        let selectionEmpty = false;
        let menuShowing = false;
        let touchStartMove = null;

        const oldFireMouseEvent = this.fireMouseEvent;

        this.fireMouseEvent = function (evtName, me, sender) {
            if (evtName === mxEvent.MOUSE_DOWN) {
                // For hit detection on edges
                me = this.updateMouseEvent(me);

                cellSelected = this.isCellSelected(me.getCell());
                selectionEmpty = this.isSelectionEmpty();
                menuShowing = this.popupMenuHandler.isMenuShowing();
                const cell = me.getCell();
                touchStartMove = mxEvent.isTouchEvent(me.getEvent()) &&
                    cell != null &&
                    this.model.isVertex(cell) &&
                    this.isCellMovable(cell) ?
                    {cell, x: me.getX(), y: me.getY()} :
                    null;
            } else if (evtName === mxEvent.MOUSE_MOVE &&
                touchStartMove != null &&
                this.graphHandler.first == null &&
                Math.max(
                    Math.abs(me.getX() - touchStartMove.x),
                    Math.abs(me.getY() - touchStartMove.y)
                ) > this.getEventTolerance()) {
                this.graphHandler.start(touchStartMove.cell, touchStartMove.x, touchStartMove.y);
            } else if (evtName === mxEvent.MOUSE_UP) {
                touchStartMove = null;
            }

            oldFireMouseEvent.apply(this, arguments);
        };

        // Shows popup menu if cell was selected or selection was empty and background was clicked
        // FIXME: Conflicts with mxPopupMenuHandler.prototype.getCellForPopupEvent in Editor.js by
        // selecting parent for selected children in groups before this check can be made.
        this.popupMenuHandler.mouseUp = mxUtils.bind(this, function (sender, me) {
            this.popupMenuHandler.popupTrigger = !this.isEditing() &&
                this.isEnabled() &&
                (me.getState() == null || !me.isSource(me.getState().control)) &&
                (this.popupMenuHandler.popupTrigger ||
                    (!menuShowing && !mxEvent.isMouseEvent(me.getEvent()) &&
                        ((selectionEmpty && me.getCell() == null &&
                                this.isSelectionEmpty()) ||
                            (cellSelected && this.isCellSelected(me.getCell())))));
            mxPopupMenuHandler.prototype.mouseUp.apply(this.popupMenuHandler,
                arguments);
        });
    };

    /**
     * Creates the keyboard event handler for the current graph and history.
     */
    mxCellEditor.prototype.saveSelection = function () {
        if (window.getSelection) {
            const sel = window.getSelection();

            if (sel.getRangeAt && sel.rangeCount) {
                const ranges = [];

                for (let i = 0, len = sel.rangeCount; i < len; ++i) {
                    ranges.push(sel.getRangeAt(i));
                }

                return ranges;
            }
        } else if (document.selection && document.selection.createRange) {
            return document.selection.createRange();
        }

        return null;
    };

    /**
     * Creates the keyboard event handler for the current graph and history.
     */
    mxCellEditor.prototype.restoreSelection = function (savedSel) {
        try {
            if (savedSel) {
                if (window.getSelection) {
                    const sel = window.getSelection();
                    sel.removeAllRanges();

                    for (let i = 0, len = savedSel.length; i < len; ++i) {
                        sel.addRange(savedSel[i]);
                    }
                } else if (document.selection && savedSel.select) {
                    savedSel.select();
                }
            }
        } catch (e) {
            // ignore
        }
    };

    /**
     * Handling of special nl2Br style for not converting newlines to breaks in HTML labels.
     * NOTE: Since it's easier to set this when the label is created we assume that it does
     * not change during the lifetime of the mxText instance.
     */
    const mxCellRendererInitializeLabel = mxCellRenderer.prototype.initializeLabel;
    mxCellRenderer.prototype.initializeLabel = function (state) {
        if (state.text != null) {
            state.text.replaceLinefeeds = mxUtils.getValue(state.style, 'nl2Br',
                '1') !== '0';
        }

        mxCellRendererInitializeLabel.apply(this, arguments);
    };

    /**
     * No dashed shapes.
     */
    mxGuide.prototype.createGuideShape = function (horizontal) {
        return new mxPolyline([], mxConstants.GUIDE_COLOR,
            mxConstants.GUIDE_STROKEWIDTH);
    };

    /**
     * HTML in-place editor
     */
    mxCellEditor.prototype.escapeCancelsEditing = false;

    const mxCellEditorStartEditing = mxCellEditor.prototype.startEditing;
    mxCellEditor.prototype.startEditing = function (cell, trigger) {
        mxCellEditorStartEditing.apply(this, arguments);

        // Overrides class in case of HTML content to add
        // dashed borders for divs and table cells
        this.textarea.className = 'mxCellEditor mxPlainTextEditor';
        this.textarea.style.lineHeight = '';

        // Stores current selection range when switching between markup and code
        this.switchSelectionState = null;

        // Selects editing cell
        this.graph.setSelectionCell(cell);
    };

    /**
     * HTML in-place editor
     */
    const cellEditorInstallListeners = mxCellEditor.prototype.installListeners;
    mxCellEditor.prototype.installListeners = function (elt) {
        cellEditorInstallListeners.apply(this, arguments);

        // Adds a reference from the clone to the original node, recursively
        function reference(node, clone) {
            clone.originalNode = node;

            node = node.firstChild;
            let child = clone.firstChild;

            while (node != null && child != null) {
                reference(node, child);
                node = node.nextSibling;
                child = child.nextSibling;
            }

            return clone;
        }

        // Checks the given node for new nodes, recursively
        function checkNode(node, clone) {
            if (node != null) {
                if (clone.originalNode !== node) {
                    cleanNode(node);
                } else {
                    node = node.firstChild;
                    clone = clone.firstChild;

                    while (node != null) {
                        const nextNode = node.nextSibling;

                        if (clone == null) {
                            cleanNode(node);
                        } else {
                            checkNode(node, clone);
                            clone = clone.nextSibling;
                        }

                        node = nextNode;
                    }
                }
            }
        }

        // Removes unused DOM nodes and attributes, recursively
        function cleanNode(node) {
            let child = node.firstChild;

            while (child != null) {
                const next = child.nextSibling;
                cleanNode(child);
                child = next;
            }

            if ((node.nodeType !== 1 ||
                    (node.nodeName !== 'BR' && node.firstChild == null)) &&
                (node.nodeType !== 3 ||
                    mxUtils.trim(mxUtils.getTextContent(node)).length === 0)) {
                node.parentNode.removeChild(node);
            } else {
                // Removes linefeeds
                if (node.nodeType === 3) {
                    mxUtils.setTextContent(node,
                        mxUtils.getTextContent(node).replace(/\n|\r/g, ''));
                }

                // Removes CSS classes and styles (for Word and Excel)
                if (node.nodeType === 1) {
                    node.removeAttribute('style');
                    node.removeAttribute('class');
                    node.removeAttribute('width');
                    node.removeAttribute('cellpadding');
                    node.removeAttribute('cellspacing');
                    node.removeAttribute('border');
                }
            }
        }

        // Handles paste from Word, Excel etc by removing styles, classnames and unused nodes
        // LATER: Fix undo/redo for paste
        if (!mxClient.IS_QUIRKS && document.documentMode !== 7 &&
            document.documentMode !== 8) {
            mxEvent.addListener(this.textarea, 'paste',
                mxUtils.bind(this, function (evt) {
                    const clone = reference(this.textarea,
                        this.textarea.cloneNode(true));

                    window.setTimeout(mxUtils.bind(this, function () {
                        checkNode(this.textarea, clone);
                    }), 0);
                }));
        }
    };

    const mxCellEditorApplyValue = mxCellEditor.prototype.applyValue;
    mxCellEditor.prototype.applyValue = function (state, value) {
        // Removes empty relative child labels in edges
        this.graph.getModel().beginUpdate();

        try {
            mxCellEditorApplyValue.apply(this, arguments);

            if (this.graph.isCellDeletable(state.cell) &&
                this.graph.model.getChildCount(state.cell) === 0) {
                const stroke = mxUtils.getValue(state.style,
                    mxConstants.STYLE_STROKECOLOR, mxConstants.NONE);
                const fill = mxUtils.getValue(state.style, mxConstants.STYLE_FILLCOLOR,
                    mxConstants.NONE);

                if (value === '' && stroke === mxConstants.NONE && fill ===
                    mxConstants.NONE) {
                    this.graph.removeCells([state.cell], false);
                }
            }
        } finally {
            this.graph.getModel().endUpdate();
        }
    };

    /**
     * Returns the background color to be used for the editing box. This returns
     * the label background for edge labels and null for all other cases.
     */
    mxCellEditor.prototype.getBackgroundColor = function (state) {
        const color = null;

        if (this.graph.getModel().isEdge(state.cell) || this.graph.getModel().isEdge(this.graph.getModel().getParent(state.cell))) {
            let color = mxUtils.getValue(state.style,
                mxConstants.STYLE_LABEL_BACKGROUNDCOLOR, null);

            if (color === mxConstants.NONE) {
                color = null;
            }
        }

        return color;
    };

    mxCellEditor.prototype.getMinimumSize = function (state) {
        const scale = this.graph.getView().scale;

        return new mxRectangle(0, 0,
            (state.text == null) ? 30 : state.text.size * scale + 20, 30);
    };

    const mxCellEditorResize = mxCellEditor.prototype.resize;
    mxCellEditor.prototype.resize = function () {
        mxCellEditorResize.apply(this, arguments);

        const state = this.editingCell ? this.graph.getView().getState(this.editingCell) : null;
        const labelNode = state?.text?.node;
        const editor = this.textarea;

        if (state == null || labelNode == null || editor == null ||
            editor.style.position !== 'absolute') {
            return;
        }

        const labelRect = labelNode.getBoundingClientRect();
        const editorRect = editor.getBoundingClientRect();

        if (labelRect.width <= 0 || labelRect.height <= 0 ||
            editorRect.width <= 0 || editorRect.height <= 0) {
            return;
        }

        const dx = labelRect.left - editorRect.left;
        const dy = labelRect.top - editorRect.top;

        if (Math.abs(dx) >= 0.5) {
            editor.style.left = `${(parseFloat(editor.style.left) || 0) + dx}px`;
        }

        if (Math.abs(dy) >= 0.5) {
            editor.style.top = `${(parseFloat(editor.style.top) || 0) + dy}px`;
        }
    };

    // Hold alt to ignore drop target
    const mxGraphHandlerMoveCells = mxGraphHandler.prototype.moveCells;

    mxGraphHandler.prototype.moveCells = function (
        cells, dx, dy, clone, target, evt) {
        if (mxEvent.isAltDown(evt)) {
            target = null;
        }

        mxGraphHandlerMoveCells.apply(this, arguments);
    };

    /**
     * Enables centered resize events.
     */
    mxVertexHandler.prototype.isCenteredEvent = function (state, me) {
        return (!(!this.graph.isSwimlane(state.cell) &&
                    this.graph.model.getChildCount(state.cell) > 0 &&
                    !this.graph.isCellCollapsed(state.cell) &&
                    mxUtils.getValue(state.style, 'recursiveResize', '1') === '1' &&
                    mxUtils.getValue(state.style, 'childLayout', null) == null) &&
                mxEvent.isControlDown(me.getEvent())) ||
            mxEvent.isMetaDown(me.getEvent());
    };

    const vertexHandlerGetHandlePadding = mxVertexHandler.prototype.getHandlePadding;
    mxVertexHandler.prototype.getHandlePadding = function () {
        let result = new mxPoint(0, 0);
        let tol = this.tolerance;
        let name = this.state.style['shape'];

        if (mxShapeRegistry.get(name) == null &&
            mxStencilRegistry.get(name) == null) {
            name = mxConstants.SHAPE_RECTANGLE;
        }

        let handlePadding = this.graph.cellEditor.getEditingCell() === this.state.cell;

        if (!handlePadding) {
            if (this.customHandles != null) {
                for (let i = 0; i < this.customHandles.length; i++) {
                    if (this.customHandles[i].shape != null &&
                        this.customHandles[i].shape.bounds != null) {
                        const b = this.customHandles[i].shape.bounds;
                        const px = b.getCenterX();
                        const py = b.getCenterY();

                        if ((Math.abs(this.state.x - px) < b.width / 2) ||
                            (Math.abs(this.state.y - py) < b.height / 2) ||
                            (Math.abs(this.state.x + this.state.width - px) < b.width / 2) ||
                            (Math.abs(this.state.y + this.state.height - py) < b.height / 2)) {
                            handlePadding = true;
                            break;
                        }
                    }
                }
            }
        }

        if (handlePadding && this.sizers != null &&
            this.sizers.length > 0 && this.sizers[0] != null) {
            tol /= 2;
            result.x = this.sizers[0].bounds.width + tol;
            result.y = this.sizers[0].bounds.height + tol;
        } else {
            result = vertexHandlerGetHandlePadding.apply(this, arguments);
        }

        return result;
    };

    /**
     * Defines the handles for the UI. Uses data-URIs to speed-up loading time where supported.
     */
    HoverIcons.prototype.mainHandle = createSvgImage(18, 18, '<circle cx="9" cy="9" r="5" stroke="#fff" fill="#007dfc" stroke-width="1"/>');
    HoverIcons.prototype.secondaryHandle = createSvgImage(16, 16, '<path d="m 8 3 L 13 8 L 8 13 L 3 8 z" stroke="#fff" fill="#fca000" stroke-width="1"/>');
    HoverIcons.prototype.fixedHandle = createSvgImage(18, 18, '<circle cx="9" cy="9" r="5" stroke="#fff" fill="#007dfc" stroke-width="1"/><path d="m 7 7 L 11 11 M 7 11 L 11 7" stroke="#fff"/>');
    HoverIcons.prototype.terminalHandle = createSvgImage(18, 18, '<circle cx="9" cy="9" r="5" stroke="#fff" fill="#007dfc" stroke-width="1"/><circle cx="9" cy="9" r="2" stroke="#fff" fill="transparent"/>');
    HoverIcons.prototype.rotationHandle = new mxImage(RotateRightSvg, 16, 16);

    mxConstraintHandler.prototype.pointImage = createSvgImage(5, 5, '<path d="m 0 0 L 5 5 M 0 5 L 5 0" stroke="#007dfc"/>');

    mxVertexHandler.prototype.handleImage = HoverIcons.prototype.mainHandle;
    mxVertexHandler.prototype.secondaryHandleImage = HoverIcons.prototype.secondaryHandle;
    mxEdgeHandler.prototype.handleImage = HoverIcons.prototype.mainHandle;
    mxEdgeHandler.prototype.terminalHandleImage = HoverIcons.prototype.terminalHandle;
    mxEdgeHandler.prototype.fixedHandleImage = HoverIcons.prototype.fixedHandle;
    mxEdgeHandler.prototype.labelHandleImage = HoverIcons.prototype.secondaryHandle;
    mxOutline.prototype.sizerImage = HoverIcons.prototype.mainHandle;

    Palette.prototype.triangleUp = HoverIcons.prototype.triangleUp;
    Palette.prototype.triangleRight = HoverIcons.prototype.triangleRight;
    Palette.prototype.triangleDown = HoverIcons.prototype.triangleDown;
    Palette.prototype.triangleLeft = HoverIcons.prototype.triangleLeft;
    Palette.prototype.refreshTarget = HoverIcons.prototype.refreshTarget;
    Palette.prototype.roundDrop = HoverIcons.prototype.roundDrop;

    // Adds rotation handle and live preview
    // mxVertexHandler.prototype.rotationEnabled = false;
    mxVertexHandler.prototype.manageSizers = true;
    mxVertexHandler.prototype.livePreview = !Graph.touchStyle;

    // Increases default rubberband opacity (default is 20)
    mxRubberband.prototype.defaultOpacity = 30;

    // Enables connections along the outline, virtual waypoints, parent highlight etc
    // mxConnectionHandler.prototype.outlineConnect = true;
    mxCellHighlight.prototype.keepOnTop = true;
    mxVertexHandler.prototype.parentHighlightEnabled = true;
    // mxVertexHandler.prototype.rotationHandleVSpacing = -20;

    mxEdgeHandler.prototype.parentHighlightEnabled = true;
    mxEdgeHandler.prototype.dblClickRemoveEnabled = true;
    mxEdgeHandler.prototype.straightRemoveEnabled = true;
    mxEdgeHandler.prototype.virtualBendsEnabled = true;
    mxEdgeHandler.prototype.mergeRemoveEnabled = true;
    mxEdgeHandler.prototype.manageLabelHandle = true;
    mxEdgeHandler.prototype.outlineConnect = true;

    // Disables adding waypoints if shift is pressed
    mxEdgeHandler.prototype.isAddVirtualBendEvent = function (me) {
        return !mxEvent.isShiftDown(me.getEvent());
    };

    // Disables custom handles if shift is pressed
    mxEdgeHandler.prototype.isCustomHandleEvent = function (me) {
        return !mxEvent.isShiftDown(me.getEvent());
    };

    const sourceContainsNode = (shape, source) =>
        shape?.node != null &&
        source != null &&
        (shape.node === source || shape.node.contains(source));
    const eventIntersectsNode = (shape, evt, tolerance = 0) => {
        const node = shape?.node;
        if (node == null || typeof node.getBoundingClientRect !== 'function') {
            return false;
        }

        const x = mxEvent.getClientX(evt);
        const y = mxEvent.getClientY(evt);
        const rect = node.getBoundingClientRect();

        return x >= rect.left - tolerance &&
            x <= rect.right + tolerance &&
            y >= rect.top - tolerance &&
            y <= rect.bottom + tolerance;
    };

    const mxEdgeHandlerGetHandleForEvent = mxEdgeHandler.prototype.getHandleForEvent;
    mxEdgeHandler.prototype.getHandleForEvent = function (me) {
        const handle = mxEdgeHandlerGetHandleForEvent.apply(this, arguments);
        if (handle != null) {
            return handle;
        }

        const source = mxEvent.getSource(me.getEvent());
        const tolerance = mxEvent.isMouseEvent(me.getEvent()) ? 1 : (this.tolerance || 0);
        if (this.virtualBends && this.isAddVirtualBendEvent(me)) {
            for (let i = 0; i < this.virtualBends.length; i++) {
                if (sourceContainsNode(this.virtualBends[i], source) ||
                    eventIntersectsNode(this.virtualBends[i], me.getEvent(), tolerance)) {
                    return mxEvent.VIRTUAL_HANDLE - i;
                }
            }
        }

        for (let i = 0; i < (this.bends || []).length; i++) {
            if (sourceContainsNode(this.bends[i], source) ||
                eventIntersectsNode(this.bends[i], me.getEvent(), tolerance)) {
                return i;
            }
        }

        if (sourceContainsNode(this.labelShape, source) ||
            eventIntersectsNode(this.labelShape, me.getEvent(), tolerance)) {
            return mxEvent.LABEL_HANDLE;
        }

        return handle;
    };

    const vertexHandlerUnion = mxVertexHandler.prototype.union;
    mxVertexHandler.prototype.union = function (bounds, dx, dy, index, gridEnabled, scale, tr, constrained, centered) {
        const result = vertexHandlerUnion.apply(this, arguments);
        const minimum = this.graph.getMinimumVertexSize?.(this.state?.cell, this.state);

        return constrainBoundsToMinimum(result, minimum == null ? null :
            new mxRectangle(0, 0, minimum.width * scale, minimum.height * scale),
            index, centered);
    };

    const graphHandlerMouseDown = mxGraphHandler.prototype.mouseDown;
    mxGraphHandler.prototype.mouseDown = function (sender, me) {
        const selectedCell = this.graph.getSelectionCell();
        const selectedHandler = selectedCell?.isEdge?.() ?
            this.graph.selectionCellsHandler?.getHandler(selectedCell) :
            null;

        if (selectedHandler?.getHandleForEvent?.(me) != null) {
            return;
        }

        graphHandlerMouseDown.apply(this, arguments);

        if (mxEvent.isTouchEvent(me.getEvent()) &&
            this.graph.isCellSelected(me.getCell()) &&
            this.graph.getSelectionCount() > 1) {
            this.delayedSelection = false;
        }
    };

    if (Graph.touchStyle) {
        const setTouchHandlePointerEvents = shape => {
            if (shape?.node != null) {
                shape.node.setAttribute('pointer-events', 'all');
            }
        };

        const vertexHandlerCreateSizer = mxVertexHandler.prototype.createSizer;
        mxVertexHandler.prototype.createSizer = function () {
            const sizer = vertexHandlerCreateSizer.apply(this, arguments);

            setTouchHandlePointerEvents(sizer);

            return sizer;
        };

        const edgeHandlerInitBend = mxEdgeHandler.prototype.initBend;
        mxEdgeHandler.prototype.initBend = function (bend) {
            edgeHandlerInitBend.apply(this, arguments);
            setTouchHandlePointerEvents(bend);
        };

        // Larger tolerance for real touch devices
        if (mxClient.IS_TOUCH || navigator.maxTouchPoints > 0 ||
            navigator.msMaxTouchPoints > 0) {
            mxShape.prototype.svgStrokeTolerance = 18;
            mxVertexHandler.prototype.tolerance = 12;
            mxEdgeHandler.prototype.tolerance = 12;
            Graph.prototype.tolerance = 12;

            mxVertexHandler.prototype.rotationHandleVSpacing = -16;

            // Implements a smaller tolerance for mouse events and a larger tolerance for touch
            // events on touch devices. The default tolerance (4px) is used for mouse events.
            mxConstraintHandler.prototype.getTolerance = function (me) {
                const evt = me.getEvent();
                const pointerType = evt?.pointerType;

                return (pointerType != null ? pointerType === 'mouse' : mxEvent.isMouseEvent(evt)) ?
                    4 :
                    this.graph.getTolerance();
            };
        }

        // One finger pans (no rubberband selection) must start regardless of mouse button
        mxPanningHandler.prototype.isPanningTrigger = function (me) {
            const evt = me.getEvent();
            const cell = me.getState() == null && !mxEvent.isMouseEvent(evt) ?
                this.graph.getCellAt(me.getGraphX(), me.getGraphY()) :
                null;

            return (me.getState() == null && cell == null && !mxEvent.isMouseEvent(evt)) ||
                (mxEvent.isPopupTrigger(evt) && (me.getState() == null ||
                mxEvent.isControlDown(evt) || mxEvent.isShiftDown(evt)));
        };

    } else {
        // Removes ctrl+shift as panning trigger for space splitting
        mxPanningHandler.prototype.isPanningTrigger = function (me) {
            const evt = me.getEvent();

            return (mxEvent.isLeftMouseButton(evt) &&
                ((this.useLeftButtonForPanning &&
                    me.getState() == null) || (mxEvent.isControlDown(evt) &&
                    !mxEvent.isShiftDown(evt)))) || (this.usePopupTrigger &&
                mxEvent.isPopupTrigger(evt));
        };
    }

    const graphHandlerMouseUp = mxGraphHandler.prototype.mouseUp;
    mxGraphHandler.prototype.mouseUp = function (sender, me) {
        if (isTouchMoveCommitEvent(this, me.getEvent()) &&
            commitActiveTouchMove(this, me.getEvent())) {
            this.consumeMouseEvent(mxEvent.MOUSE_UP, me);
            return;
        }

        graphHandlerMouseUp.apply(this, arguments);
    };

    // Overrides/extends rubberband for space handling with Ctrl+Shift(+Alt) drag ("scissors tool")
    mxRubberband.prototype.isSpaceEvent = function (me) {
        return this.graph.isEnabled() &&
            !this.graph.isCellLocked(this.graph.getDefaultParent()) &&
            mxEvent.isControlDown(me.getEvent()) &&
            mxEvent.isShiftDown(me.getEvent());
    };

    // Handles moving of cells in both half panes
    mxRubberband.prototype.mouseUp = function (sender, me) {
        const execute = this.div != null && this.div.style.display !== 'none';

        let x0 = null;
        let y0 = null;
        let dx = null;
        let dy = null;

        if (this.first != null && this.currentX != null && this.currentY != null) {
            x0 = this.first.x;
            y0 = this.first.y;
            dx = (this.currentX - x0) / this.graph.view.scale;
            dy = (this.currentY - y0) / this.graph.view.scale;

            if (!mxEvent.isAltDown(me.getEvent())) {
                dx = this.graph.snap(dx);
                dy = this.graph.snap(dy);

                if (!this.graph.isGridEnabled()) {
                    if (Math.abs(dx) < this.graph.tolerance) {
                        dx = 0;
                    }

                    if (Math.abs(dy) < this.graph.tolerance) {
                        dy = 0;
                    }
                }
            }
        }

        this.reset();

        if (execute) {
            if (mxEvent.isAltDown(me.getEvent()) &&
                this.graph.isToggleEvent(me.getEvent())) {
                const rect = new mxRectangle(this.x, this.y, this.width, this.height);
                const cells = this.graph.getCells(rect.x, rect.y, rect.width,
                    rect.height);

                this.graph.removeSelectionCells(cells);
            } else if (this.isSpaceEvent(me)) {
                this.graph.model.beginUpdate();
                try {
                    const cells = this.graph.getCellsBeyond(x0, y0,
                        this.graph.getDefaultParent(), true, true);

                    for (let i = 0; i < cells.length; i++) {
                        if (this.graph.isCellMovable(cells[i])) {
                            const tmp = this.graph.view.getState(cells[i]);
                            let geo = this.graph.getCellGeometry(cells[i]);

                            if (tmp != null && geo != null) {
                                geo = geo.clone();
                                geo.translate(dx, dy);
                                this.graph.model.setGeometry(cells[i], geo);
                            }
                        }
                    }
                } finally {
                    this.graph.model.endUpdate();
                }
            } else {
                const rect = new mxRectangle(this.x, this.y, this.width, this.height);
                this.graph.selectRegion(rect, me.getEvent());
            }

            me.consume();
        }
    };

    // Handles preview for creating/removing space in diagram
    mxRubberband.prototype.mouseMove = function (sender, me) {
        if (!me.isConsumed() && this.first != null) {
            const origin = mxUtils.getScrollOrigin(this.graph.container);
            const offset = mxUtils.getOffset(this.graph.container);
            origin.x -= offset.x;
            origin.y -= offset.y;
            const x = me.getX() + origin.x;
            const y = me.getY() + origin.y;
            const dx = this.first.x - x;
            const dy = this.first.y - y;
            const tol = this.graph.tolerance;

            if (this.div != null || Math.abs(dx) > tol || Math.abs(dy) > tol) {
                if (this.div == null) {
                    this.div = this.createShape();
                }

                // Clears selection while rubberbanding. This is required because
                // the event is not consumed in mouseDown.
                mxUtils.clearSelection();
                this.update(x, y);

                if (this.isSpaceEvent(me)) {
                    const right = this.x + this.width;
                    const bottom = this.y + this.height;
                    const scale = this.graph.view.scale;

                    if (!mxEvent.isAltDown(me.getEvent())) {
                        this.width = this.graph.snap(this.width / scale) * scale;
                        this.height = this.graph.snap(this.height / scale) * scale;

                        if (!this.graph.isGridEnabled()) {
                            if (this.width < this.graph.tolerance) {
                                this.width = 0;
                            }

                            if (this.height < this.graph.tolerance) {
                                this.height = 0;
                            }
                        }

                        if (this.x < this.first.x) {
                            this.x = right - this.width;
                        }

                        if (this.y < this.first.y) {
                            this.y = bottom - this.height;
                        }
                    }

                    this.div.style.borderStyle = 'dashed';
                    this.div.style.backgroundColor = 'white';
                    this.div.style.left = this.x + 'px';
                    this.div.style.top = this.y + 'px';
                    this.div.style.width = Math.max(0, this.width) + 'px';
                    this.div.style.height = this.graph.container.clientHeight + 'px';
                    this.div.style.borderWidth = (this.width <= 0) ?
                        '0px 1px 0px 0px' :
                        '0px 1px 0px 1px';

                    if (this.secondDiv == null) {
                        this.secondDiv = this.div.cloneNode(true);
                        this.div.parentNode.appendChild(this.secondDiv);
                    }

                    this.secondDiv.style.left = this.x + 'px';
                    this.secondDiv.style.top = this.y + 'px';
                    this.secondDiv.style.width = this.graph.container.clientWidth + 'px';
                    this.secondDiv.style.height = Math.max(0, this.height) + 'px';
                    this.secondDiv.style.borderWidth = (this.height <= 0) ?
                        '1px 0px 0px 0px' :
                        '1px 0px 1px 0px';
                } else {
                    // Hides second div and restores style
                    this.div.style.backgroundColor = '';
                    this.div.style.borderWidth = '';
                    this.div.style.borderStyle = '';

                    if (this.secondDiv != null) {
                        this.secondDiv.parentNode.removeChild(this.secondDiv);
                        this.secondDiv = null;
                    }
                }

                me.consume();
            }
        }
    };

    // Removes preview
    const mxRubberbandReset = mxRubberband.prototype.reset;
    mxRubberband.prototype.reset = function () {
        if (this.secondDiv != null) {
            this.secondDiv.parentNode.removeChild(this.secondDiv);
            this.secondDiv = null;
        }

        mxRubberbandReset.apply(this, arguments);
    };

    // Timer-based activation of outline connect in connection handler
    let startTime = new Date().getTime();
    let timeOnTarget = 0;

    mxEdgeHandler.prototype.createSelectionShape = function () {
        const shape = new this.state.shape.constructor();
        shape.outline = false;
        shape.apply(this.state);
        applyRoutePreviewMarkerStyle(shape, this.state);
        shape.isDashed = this.isSelectionDashed();
        shape.stroke = this.getSelectionColor();
        shape.isShadow = false;

        return shape;
    };

    const mxEdgeHandlerDrawPreview = mxEdgeHandler.prototype.drawPreview;
    mxEdgeHandler.prototype.drawPreview = function () {
        mxEdgeHandlerDrawPreview.apply(this, arguments);
        applyRoutePreviewMarkerStyle(this.shape, this.state);
        this.shape?.redraw?.();
    };

    const mxEdgeHandlerChangePoints = mxEdgeHandler.prototype.changePoints;
    mxEdgeHandler.prototype.changePoints = function (edge, points, clone) {
        const result = mxEdgeHandlerChangePoints.apply(this, arguments);
        return result;
    };

    const mxEdgeHandlerUpdatePreviewState = mxEdgeHandler.prototype.updatePreviewState;

    mxEdgeHandler.prototype.updatePreviewState = function (
        edge, point, terminalState, me) {
        mxEdgeHandlerUpdatePreviewState.apply(this, arguments);

        if (terminalState !== this.currentTerminalState) {
            startTime = new Date().getTime();
            timeOnTarget = 0;
        } else {
            timeOnTarget = new Date().getTime() - startTime;
        }

        this.currentTerminalState = terminalState;
    };

    const mxEdgeHandlerIsHandleEnabled = mxEdgeHandler.prototype.isHandleEnabled;
    mxEdgeHandler.prototype.isHandleEnabled = function (index) {
        if (this.graph.editable) {
            return mxEdgeHandlerIsHandleEnabled.apply(this, arguments);
        }

        return false;
    };

    const mxEdgeHandlerIsHandleVisible = mxEdgeHandler.prototype.isHandleVisible;
    mxEdgeHandler.prototype.isHandleVisible = function (index) {
        if (this.graph.editable) {
            return mxEdgeHandlerIsHandleVisible.apply(this, arguments);
        }

        return false;
    };

    // Timer-based outline connect
    const mxEdgeHandlerIsOutlineConnectEvent = mxEdgeHandler.prototype.isOutlineConnectEvent;

    mxEdgeHandler.prototype.isOutlineConnectEvent = function (me) {
        return (this.currentTerminalState != null && me.getState() ===
                this.currentTerminalState && timeOnTarget > 2000) ||
            ((this.currentTerminalState == null ||
                    mxUtils.getValue(this.currentTerminalState.style, 'outlineConnect',
                        '1') !== '0') &&
                mxEdgeHandlerIsOutlineConnectEvent.apply(this, arguments));
    };

    // Disables custom handles if shift is pressed
    mxVertexHandler.prototype.isCustomHandleEvent = function (me) {
        return !mxEvent.isShiftDown(me.getEvent());
    };

    // Shows secondary handle for fixed connection points
    mxEdgeHandler.prototype.createHandleShape = function (index, virtual) {
        const source = index != null && index === 0;
        const terminalState = this.state.getVisibleTerminalState(source);
        const c = (index != null &&
            (index === 0 || index >= this.state.absolutePoints.length - 1 ||
                (this.constructor === mxElbowEdgeHandler && index === 2))) ?
            this.graph.getConnectionConstraint(this.state, terminalState, source) :
            null;
        const pt = (c != null) ?
            this.graph.getConnectionPoint(
                this.state.getVisibleTerminalState(source), c) :
            null;
        const img = (pt != null) ?
            this.fixedHandleImage :
            ((c != null && terminalState != null) ?
                this.terminalHandleImage : (this.handleImage || HoverIcons.prototype.mainHandle));

        if (img != null) {
            const shape = new mxImageShape(
                new mxRectangle(0, 0, img.width, img.height), img.src);

            // Allows HTML rendering of the images
            shape.preserveImageAspect = false;

            return shape;
        } else {
            let s = mxConstants.HANDLE_SIZE;

            if (this.preferHtml) {
                s -= 1;
            }

            return new mxRectangleShape(new mxRectangle(0, 0, s, s),
                mxConstants.HANDLE_FILLCOLOR, mxConstants.HANDLE_STROKECOLOR);
        }
    };

    const vertexHandlerCreateSizerShape = mxVertexHandler.prototype.createSizerShape;
    mxVertexHandler.prototype.createSizerShape = function (
        bounds, index, fillColor) {
        this.handleImage = (index === mxEvent.ROTATION_HANDLE) ?
            HoverIcons.prototype.rotationHandle :
            (index === mxEvent.LABEL_HANDLE) ?
                this.secondaryHandleImage :
                (this.handleImage || HoverIcons.prototype.mainHandle);

        return vertexHandlerCreateSizerShape.apply(this, arguments);
    };

    // Special case for single edge label handle moving in which case the text bounding box is used
    const mxGraphHandlerGetBoundingBox = mxGraphHandler.prototype.getBoundingBox;
    mxGraphHandler.prototype.getBoundingBox = function (cells) {
        if (cells != null && cells.length === 1) {
            const model = this.graph.getModel();
            const parent = model.getParent(cells[0]);
            const geo = this.graph.getCellGeometry(cells[0]);

            if (model.isEdge(parent) && geo != null && geo.relative) {
                const state = this.graph.view.getState(cells[0]);

                if (state != null && state.width < 2 && state.height < 2 &&
                    state.text != null && state.text.boundingBox != null) {
                    return mxRectangle.fromRectangle(state.text.boundingBox);
                }
            }
        }

        return mxGraphHandlerGetBoundingBox.apply(this, arguments);
    };

    // Uses text bounding box for edge labels
    const mxVertexHandlerGetSelectionBounds = mxVertexHandler.prototype.getSelectionBounds;
    mxVertexHandler.prototype.getSelectionBounds = function (state) {
        const model = this.graph.getModel();
        const parent = model.getParent(state.cell);
        const geo = this.graph.getCellGeometry(state.cell);

        if (model.isEdge(parent) && geo != null && geo.relative && state.width <
            2 && state.height < 2 && state.text != null && state.text.boundingBox !=
            null) {
            const bbox = state.text.unrotatedBoundingBox || state.text.boundingBox;

            return new mxRectangle(Math.round(bbox.x), Math.round(bbox.y),
                Math.round(bbox.width), Math.round(bbox.height));
        } else {
            return mxVertexHandlerGetSelectionBounds.apply(this, arguments);
        }
    };

    // Redirects moving of edge labels to mxGraphHandler by not starting here.
    // This will use the move preview of mxGraphHandler (see above).
    const mxVertexHandlerMouseDown = mxVertexHandler.prototype.mouseDown;
    mxVertexHandler.prototype.mouseDown = function (sender, me) {
        const model = this.graph.getModel();
        const parent = model.getParent(this.state.cell);
        const geo = this.graph.getCellGeometry(this.state.cell);

        // Lets rotation events through
        const handle = this.getHandleForEvent(me);

        if (handle === mxEvent.ROTATION_HANDLE || !model.isEdge(parent) || geo ==
            null || !geo.relative ||
            this.state == null || this.state.width >= 2 || this.state.height >= 2) {
            mxVertexHandlerMouseDown.apply(this, arguments);
        }
    };

    // Shows rotation handle for edge labels.
    mxVertexHandler.prototype.isRotationHandleVisible = function () {
        return this.graph.isEnabled() && this.rotationEnabled &&
            this.graph.isCellRotatable(this.state.cell) &&
            (mxGraphHandler.prototype.maxCells <= 0 ||
                this.graph.getSelectionCount() < mxGraphHandler.prototype.maxCells);
    };

    const vertexHandlerMouseMove = mxVertexHandler.prototype.mouseMove;

    // Workaround for "isConsumed not defined" in MS Edge is to use arguments
    mxVertexHandler.prototype.mouseMove = function (sender, me) {
        vertexHandlerMouseMove.apply(this, arguments);

        if (this.graph.graphHandler.first != null) {
            if (this.rotationShape != null && this.rotationShape.node != null) {
                this.rotationShape.node.style.display = 'none';
            }
        }
    };

    const vertexHandlerMouseUp = mxVertexHandler.prototype.mouseUp;
    mxVertexHandler.prototype.mouseUp = function (sender, me) {
        vertexHandlerMouseUp.apply(this, arguments);

        // Shows rotation handle only if one vertex is selected
        if (this.rotationShape != null && this.rotationShape.node != null) {
            this.rotationShape.node.style.display = (this.graph.getSelectionCount() ===
                1) ? '' : 'none';
        }
    };

    const edgeHandlerInit = mxEdgeHandler.prototype.init;
    mxEdgeHandler.prototype.init = function () {
        edgeHandlerInit.apply(this, arguments);

        // Disables connection points
        this.constraintHandler.isEnabled = mxUtils.bind(this, function () {
            return this.state.view.graph.connectionHandler.isEnabled();
        });
    };

    // Disables connection points
    const connectionHandlerInit = mxConnectionHandler.prototype.init;
    mxConnectionHandler.prototype.init = function () {
        connectionHandlerInit.apply(this, arguments);

        this.constraintHandler.isEnabled = mxUtils.bind(this, function () {
            return this.graph.connectionHandler.isEnabled();
        });
    };

    const vertexHandlerReset = mxVertexHandler.prototype.reset;
    mxVertexHandler.prototype.reset = function () {
        vertexHandlerReset.apply(this, arguments);

        // Shows rotation handle only if one vertex is selected
        if (this.rotationShape != null && this.rotationShape.node != null) {
            this.rotationShape.node.style.display = (this.graph.getSelectionCount() ===
                1) ? '' : 'none';
        }
    };

    const vertexHandlerDestroy = mxVertexHandler.prototype.destroy;
    mxVertexHandler.prototype.destroy = function () {
        vertexHandlerDestroy.apply(this, arguments);

        if (this.selectionHandler != null) {
            this.graph.getSelectionModel().removeListener(this.selectionHandler);
            this.selectionHandler = null;
        }

        if (this.changeHandler != null) {
            this.graph.getModel().removeListener(this.changeHandler);
            this.changeHandler = null;
        }

        if (this.editingHandler != null) {
            this.graph.removeListener(this.editingHandler);
            this.editingHandler = null;
        }
    };

    const edgeHandlerDestroy = mxEdgeHandler.prototype.destroy;
    mxEdgeHandler.prototype.destroy = function () {
        edgeHandlerDestroy.apply(this, arguments);

        if (this.selectionHandler != null) {
            this.graph.getSelectionModel().removeListener(this.selectionHandler);
            this.selectionHandler = null;
        }

        if (this.changeHandler != null) {
            this.graph.getModel().removeListener(this.changeHandler);
            this.changeHandler = null;
        }
    };
})();
